import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import DOMPurify from "dompurify";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { Toaster, toast } from "sonner";
import { 
  CheckCircle, XCircle, Clock, Users, UserCheck, UserX, 
  Eye, Loader2, RefreshCw, Phone, MapPin, Briefcase, 
  Shield, LogOut, Plus, Trash2, Tag, UserPlus, Edit, Save, PhoneCall,
  Download, FileText, FileSpreadsheet, File, BarChart3, Vote, Bell, Image as ImageIcon,
  Building2, Mail, Globe, Facebook, Instagram, Twitter, Linkedin, Youtube, Target,
  Key, GitBranch, ChevronLeft, ChevronRight, Heart, Search, X, Plane, Copy, BookOpen
} from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

// Create admin API instance outside component to prevent recreation
const adminApi = axios.create({ baseURL: API });

// Add request interceptor for auth token
adminApi.interceptors.request.use((config) => {
  const token = localStorage.getItem("adminToken");
  console.log("AdminApi Request:", config.url, "Token exists:", !!token);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add response interceptor for debugging
adminApi.interceptors.response.use(
  (response) => {
    console.log("AdminApi Response OK:", response.config.url, response.status);
    return response;
  },
  (error) => {
    console.error("AdminApi Error:", error.config?.url, error.response?.status, error.response?.data);
    return Promise.reject(error);
  }
);

const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
  "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram",
  "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
  "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Delhi", "Jammu and Kashmir", "Ladakh", "Puducherry", "Chandigarh"
];

const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];

const RAJASTHAN_PLACES = [
  "Ajmer", "Alwar", "Banswara", "Baran", "Barmer", "Bharatpur", "Bhilwara", "Bikaner",
  "Bundi", "Chittorgarh", "Churu", "Dausa", "Dholpur", "Dungarpur", "Ganganagar",
  "Hanumangarh", "Jaipur", "Jaisalmer", "Jalore", "Jhalawar", "Jhunjhunu", "Jodhpur",
  "Karauli", "Kota", "Nagaur", "Pali", "Pratapgarh", "Rajsamand", "Sawai Madhopur",
  "Sikar", "Sirohi", "Tonk", "Udaipur", "Beawar", "Bhiwadi", "Didwana", "Fatehpur",
  "Gangapur City", "Hindaun", "Kishangarh", "Lachhmangarh", "Makrana", "Nathdwara",
  "Nawalgarh", "Nimbahera", "Nokha", "Phalodi", "Pilani", "Rajgarh", "Ratangarh",
  "Sadulpur", "Sanganer", "Sardarshahr", "Shahpura", "Sujangarh", "Suratgarh", "Other"
];

const AdminPanelPage = () => {
  const navigate = useNavigate();
  
  const [admin, setAdmin] = useState(null);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [prefixes, setPrefixes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [pendingPage, setPendingPage] = useState(1);
  const [pendingTotalPages, setPendingTotalPages] = useState(1);
  const [pendingTotal, setPendingTotal] = useState(0);
  const [verifiedTotal, setVerifiedTotal] = useState(0);
  const [rejectedTotal, setRejectedTotal] = useState(0);
  // Verified & Rejected separate lists with pagination
  const [verifiedUsers, setVerifiedUsers] = useState([]);
  const [verifiedPage, setVerifiedPage] = useState(1);
  const [verifiedTotalPages, setVerifiedTotalPages] = useState(1);
  const [rejectedUsers, setRejectedUsers] = useState([]);
  const [rejectedPage, setRejectedPage] = useState(1);
  const [rejectedTotalPages, setRejectedTotalPages] = useState(1);
  const ITEMS_PER_PAGE = 20;
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editUserData, setEditUserData] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectionNote, setRejectionNote] = useState("");
  const [activeTab, setActiveTab] = useState("users");
  
  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  
  // Popup management state
  const [popupSettings, setPopupSettings] = useState({ is_active: false, image: "", title: "" });
  const [popupImagePreview, setPopupImagePreview] = useState("");
  const [showPopupDialog, setShowPopupDialog] = useState(false);
  const [popupLoading, setPopupLoading] = useState(false);
  // Travel forms state
  const [travelForms, setTravelForms] = useState([]);
  const [travelFormsTotal, setTravelFormsTotal] = useState(0);
  const [travelFormsPage, setTravelFormsPage] = useState(1);
  
  // Kuldevi forms state
  const [kuldeviForms, setKuldeviForms] = useState([]);
  const [kuldeviFormsTotal, setKuldeviFormsTotal] = useState(0);
  const [kuldeviFormsPage, setKuldeviFormsPage] = useState(1);
  
  // Admin creation
  const [showAdminDialog, setShowAdminDialog] = useState(false);
  const [newAdmin, setNewAdmin] = useState({ username: "", password: "", name: "" });
  
  // Prefix creation
  const [showPrefixDialog, setShowPrefixDialog] = useState(false);
  const [newPrefix, setNewPrefix] = useState({ value: "", label: "" });
  
  // Download state
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);

  // Poll state
  const [polls, setPolls] = useState([]);
  const [showPollDialog, setShowPollDialog] = useState(false);
  const [_editingPoll, _setEditingPoll] = useState(null);
  const [newPoll, setNewPoll] = useState({ question: "", options: ["", ""] });
  const [pollLoading, setPollLoading] = useState(false);

  // Updates state
  const [updates, setUpdates] = useState([]);
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const [editingUpdate, setEditingUpdate] = useState(null);
  const [newUpdate, setNewUpdate] = useState({ title: "", content: "", images: [] });
  const [updateLoading, setUpdateLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState([]);
  const [showLikesDialog, setShowLikesDialog] = useState(false);
  const [selectedUpdateLikes, setSelectedUpdateLikes] = useState({ update: null, likes: [] });
  const [likesLoading, setLikesLoading] = useState(false);

  // Delete user state
  const [showDeleteUserDialog, setShowDeleteUserDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Change credentials state
  const [showCredentialsDialog, setShowCredentialsDialog] = useState(false);
  const [credentialsData, setCredentialsData] = useState({
    current_password: "",
    new_username: "",
    new_password: "",
    confirm_password: ""
  });
  const [credentialsLoading, setCredentialsLoading] = useState(false);

  // About page state
  const [aboutData, setAboutData] = useState({
    organization_name: "LUNAWAT FOUNDATION",
    description: "",
    history: "",
    mission: "",
    vision: "",
    contact_address: "",
    contact_phone: "",
    contact_email: "",
    facebook_url: "",
    instagram_url: "",
    twitter_url: "",
    linkedin_url: "",
    youtube_url: "",
    website_url: "",
    team_members: [],
    gallery_images: []
  });
  const [aboutLoading, setAboutLoading] = useState(false);
  const [newTeamMember, setNewTeamMember] = useState({ name: "", designation: "", photo: "" });

  // Family Tree state
  const [familyTrees, setFamilyTrees] = useState([]);
  const [verifiedUsersForFamily, setVerifiedUsersForFamily] = useState([]);
  const [showCreateFamilyDialog, setShowCreateFamilyDialog] = useState(false);
  const [newFamilyTree, setNewFamilyTree] = useState({
    family_name: "",
    members: []
  });
  const [familyMemberSearch, setFamilyMemberSearch] = useState("");
  const [selectedRelationship, setSelectedRelationship] = useState("");
  const [familyLoading, setFamilyLoading] = useState(false);
  const [showVerifyFamilyDialog, setShowVerifyFamilyDialog] = useState(false);
  const [selectedFamilyTree, setSelectedFamilyTree] = useState(null);
  const [familyRejectionNote, setFamilyRejectionNote] = useState("");

  const RELATIONSHIP_TYPES = [
    "Self", "Father", "Mother", "Grandfather", "Grandmother", 
    "Son", "Daughter", "Daughter-in-law", "Son-in-law",
    "Grandson", "Granddaughter", "Brother", "Sister",
    "Uncle", "Aunt", "Nephew", "Niece", "Cousin",
    "Husband", "Wife"
  ];

  // Handle 401 responses
  useEffect(() => {
    const interceptor = adminApi.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem("adminToken");
          localStorage.removeItem("adminUser");
          navigate("/admin-login");
        }
        return Promise.reject(error);
      }
    );
    return () => adminApi.interceptors.response.eject(interceptor);
  }, [navigate]);

  useEffect(() => {
    const adminToken = localStorage.getItem("adminToken");
    const adminUser = localStorage.getItem("adminUser");
    
    if (!adminToken) {
      navigate("/admin-login");
      return;
    }
    
    if (adminUser) {
      setAdmin(JSON.parse(adminUser));
    }
    
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const fetchData = async (page = currentPage, pendPage = pendingPage) => {
    setLoading(true);
    try {
      // Only fetch users + stats on initial load (fast)
      const [pendingRes, allRes, statsRes] = await Promise.all([
        adminApi.get(`/admin/pending-users?page=${pendPage}&limit=${ITEMS_PER_PAGE}`),
        adminApi.get(`/admin/all-users?page=${page}&limit=${ITEMS_PER_PAGE}`),
        adminApi.get("/admin/stats").catch(() => ({ data: null }))
      ]);
      
      // Handle paginated all-users response
      if (allRes.data.users) {
        setAllUsers(allRes.data.users);
        setTotalPages(allRes.data.total_pages || 1);
        setCurrentPage(allRes.data.page || 1);
        setTotalUsers(allRes.data.total || 0);
      } else if (Array.isArray(allRes.data)) {
        setAllUsers(allRes.data);
        setTotalUsers(allRes.data.length);
      }
      
      // Handle paginated pending-users response
      if (pendingRes.data.users) {
        setPendingUsers(pendingRes.data.users);
        setPendingTotalPages(pendingRes.data.total_pages || 1);
        setPendingPage(pendingRes.data.page || 1);
        setPendingTotal(pendingRes.data.total || 0);
      } else if (Array.isArray(pendingRes.data)) {
        setPendingUsers(pendingRes.data);
        setPendingTotal(pendingRes.data.length);
      }
      
      // Use stats if available
      if (statsRes.data) {
        setTotalUsers(statsRes.data.total || 0);
        setPendingTotal(statsRes.data.pending || 0);
        setVerifiedTotal(statsRes.data.verified || 0);
        setRejectedTotal(statsRes.data.rejected || 0);
      }
    } catch (error) {
      console.error("Failed to load admin data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  // Lazy load settings data only when a settings tab is clicked
  const fetchSettingsData = async (tab) => {
    try {
      if (tab === "admins" && admins.length === 0) {
        const res = await adminApi.get("/admin/admins");
        setAdmins(res.data);
      } else if (tab === "prefixes" && prefixes.length === 0) {
        const res = await adminApi.get("/admin/prefixes");
        setPrefixes(res.data);
      } else if (tab === "polls" && polls.length === 0) {
        const res = await adminApi.get("/admin/polls");
        setPolls(res.data);
      } else if (tab === "updates" && updates.length === 0) {
        const res = await adminApi.get("/admin/updates");
        setUpdates(res.data);
      } else if (tab === "about" && !aboutData) {
        try {
          const res = await adminApi.get("/admin/about");
          setAboutData(res.data);
        } catch (_e) { console.log("About data not found"); }
      } else if (tab === "family-tree" && familyTrees.length === 0) {
        try {
          const [familyRes, usersRes] = await Promise.all([
            adminApi.get("/admin/family-trees"),
            adminApi.get("/admin/verified-users-for-family")
          ]);
          setFamilyTrees(familyRes.data);
          setVerifiedUsersForFamily(usersRes.data);
        } catch (_e) { console.log("Family tree data not found"); }
      } else if (tab === "popup") {
        fetchPopupSettings();
      } else if (tab === "travel-forms") {
        fetchTravelForms();
      } else if (tab === "kuldevi-forms") {
        fetchKuldeviForms();
      }
    } catch (error) {
      console.error(`Failed to load ${tab} data:`, error);
    }
  };

  // Handle tab changes with lazy loading
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === "verified") {
      fetchVerifiedUsers(verifiedPage);
    } else if (tab === "rejected") {
      fetchRejectedUsers(rejectedPage);
    } else {
      fetchSettingsData(tab);
    }
  };

  // Fetch verified users with pagination
  const fetchVerifiedUsers = async (page = 1) => {
    try {
      const res = await adminApi.get(`/admin/all-users?status=verified&page=${page}&limit=${ITEMS_PER_PAGE}`);
      if (res.data.users) {
        setVerifiedUsers(res.data.users);
        setVerifiedPage(res.data.page || 1);
        setVerifiedTotalPages(res.data.total_pages || 1);
        setVerifiedTotal(res.data.total || 0);
      }
    } catch (error) {
      toast.error("Failed to load verified users");
    }
  };

  // Fetch rejected users with pagination
  const fetchRejectedUsers = async (page = 1) => {
    try {
      const res = await adminApi.get(`/admin/all-users?status=rejected&page=${page}&limit=${ITEMS_PER_PAGE}`);
      if (res.data.users) {
        setRejectedUsers(res.data.users);
        setRejectedPage(res.data.page || 1);
        setRejectedTotalPages(res.data.total_pages || 1);
        setRejectedTotal(res.data.total || 0);
      }
    } catch (error) {
      toast.error("Failed to load rejected users");
    }
  };

  // Search users across all statuses
  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (!query.trim()) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }
    setIsSearching(true);
    try {
      const res = await adminApi.get(`/admin/all-users?search=${encodeURIComponent(query.trim())}&page=1&limit=50`);
      setSearchResults(res.data.users || []);
    } catch (error) {
      console.error("Search failed:", error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const clearSearch = () => {
    setSearchQuery("");
    setSearchResults([]);
    setIsSearching(false);
  };

  // Popup management handlers
  const fetchPopupSettings = async () => {
    try {
      const res = await adminApi.get("/admin/popup");
      setPopupSettings(res.data);
      setPopupImagePreview(res.data.image || "");
    } catch (_e) { /* popup not configured yet */ }
  };

  const handlePopupImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result;
      setPopupSettings(prev => ({ ...prev, image: base64 }));
      setPopupImagePreview(base64);
    };
    reader.readAsDataURL(file);
  };

  const handleSavePopup = async () => {
    setPopupLoading(true);
    try {
      await adminApi.put("/admin/popup", popupSettings);
      toast.success(popupSettings.is_active ? "Popup activated!" : "Popup saved (inactive)");
      setShowPopupDialog(false);
    } catch (error) {
      toast.error("Failed to save popup settings");
    } finally {
      setPopupLoading(false);
    }
  };

  const handleRemovePopup = async () => {
    setPopupLoading(true);
    try {
      await adminApi.put("/admin/popup", { is_active: false, image: "", title: "" });
      setPopupSettings({ is_active: false, image: "", title: "" });
      setPopupImagePreview("");
      toast.success("Popup removed");
      setShowPopupDialog(false);
    } catch (error) {
      toast.error("Failed to remove popup");
    } finally {
      setPopupLoading(false);
    }
  };


  // Travel forms handlers
  const fetchTravelForms = async (page = 1) => {
    try {
      const res = await adminApi.get(`/admin/travel-forms?page=${page}&limit=20`);
      setTravelForms(res.data.forms || []);
      setTravelFormsTotal(res.data.total || 0);
      setTravelFormsPage(res.data.page || 1);
    } catch (error) {
      console.error("Failed to load travel forms:", error);
    }
  };

  const handleDeleteTravelForm = async (formId) => {
    if (!window.confirm("Delete this travel form submission?")) return;
    try {
      await adminApi.delete(`/admin/travel-forms/${formId}`);
      toast.success("Form deleted");
      fetchTravelForms(travelFormsPage);
    } catch (error) {
      toast.error("Failed to delete form");
    }
  };

  const TRANSPORT_LABELS = {
    flight: "Flight", train: "Train", personal_vehicle: "Personal Vehicle",
    company_vehicle: "Company Vehicle", public_transit: "Public Transit / Bus", other: "Other"
  };

  // Kuldevi Forms functions
  const fetchKuldeviForms = async (page = 1) => {
    try {
      const res = await adminApi.get(`/admin/kuldevi-forms?page=${page}&limit=20`);
      setKuldeviForms(res.data.forms || []);
      setKuldeviFormsTotal(res.data.total || 0);
      setKuldeviFormsPage(res.data.page || 1);
    } catch (error) {
      console.error("Failed to load kuldevi forms:", error);
    }
  };

  const handleDeleteKuldeviForm = async (formId) => {
    if (!window.confirm("Delete this Kuldevi form submission?")) return;
    try {
      await adminApi.delete(`/admin/kuldevi-forms/${formId}`);
      toast.success("Form deleted");
      fetchKuldeviForms(kuldeviFormsPage);
    } catch (error) {
      toast.error("Failed to delete form");
    }
  };

  const EVIDENCE_TYPE_LABELS = {
    bahi_bhat: "बही-भाट वंशावली",
    tamrapatra: "ताम्रपत्र",
    shilalekh: "शिलालेख",
    ancient_statue: "प्राचीन प्रतिमा/चित्र",
    oral_tradition: "बुजुर्गों की मौखिक परंपरा",
    ancient_book: "प्राचीन पुस्तक",
    other: "अन्य"
  };



  // Page change handlers - only re-fetch the specific list
  const handlePageChange = async (newPage) => {
    setCurrentPage(newPage);
    try {
      const res = await adminApi.get(`/admin/all-users?page=${newPage}&limit=${ITEMS_PER_PAGE}`);
      if (res.data.users) {
        setAllUsers(res.data.users);
        setTotalPages(res.data.total_pages || 1);
        setCurrentPage(res.data.page || 1);
        setTotalUsers(res.data.total || 0);
      }
    } catch (error) {
      toast.error("Failed to load users");
    }
  };

  const handlePendingPageChange = async (newPage) => {
    setPendingPage(newPage);
    try {
      const res = await adminApi.get(`/admin/pending-users?page=${newPage}&limit=${ITEMS_PER_PAGE}`);
      if (res.data.users) {
        setPendingUsers(res.data.users);
        setPendingTotalPages(res.data.total_pages || 1);
        setPendingPage(res.data.page || 1);
        setPendingTotal(res.data.total || 0);
      }
    } catch (error) {
      toast.error("Failed to load pending users");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminUser");
    navigate("/admin-login");
  };

  // Family Tree Functions
  const handleAddFamilyMember = (user) => {
    if (!selectedRelationship) {
      toast.error("Please select a relationship first");
      return;
    }
    
    // Check if user is already added
    if (newFamilyTree.members.some(m => m.user_id === user.id)) {
      toast.error("This member is already added");
      return;
    }

    const fullName = [user.first_name, user.middle_name, user.last_name].filter(Boolean).join(" ");
    const newMember = {
      user_id: user.id,
      lf_id: user.member_code,
      name: fullName,
      relationship: selectedRelationship,
      avatar: user.avatar
    };
    
    setNewFamilyTree(prev => ({
      ...prev,
      members: [...prev.members, newMember]
    }));
    setFamilyMemberSearch("");
    setSelectedRelationship("");
  };

  const handleRemoveFamilyMember = (index) => {
    setNewFamilyTree(prev => ({
      ...prev,
      members: prev.members.filter((_, i) => i !== index)
    }));
  };

  const handleCreateFamilyTree = async () => {
    if (!newFamilyTree.family_name.trim()) {
      toast.error("Please enter a family name");
      return;
    }
    if (newFamilyTree.members.length < 1) {
      toast.error("Please add at least one family member");
      return;
    }

    setFamilyLoading(true);
    try {
      await adminApi.post("/admin/family-trees", newFamilyTree);
      toast.success("Family tree created and submitted for verification");
      setShowCreateFamilyDialog(false);
      setNewFamilyTree({ family_name: "", members: [] });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create family tree");
    } finally {
      setFamilyLoading(false);
    }
  };

  const handleVerifyFamilyTree = async (status) => {
    if (!selectedFamilyTree) return;

    setFamilyLoading(true);
    try {
      await adminApi.put(`/admin/family-trees/${selectedFamilyTree.id}/verify`, {
        status,
        rejection_note: status === "rejected" ? familyRejectionNote : ""
      });
      toast.success(status === "verified" ? "Family tree verified!" : "Family tree rejected");
      setShowVerifyFamilyDialog(false);
      setSelectedFamilyTree(null);
      setFamilyRejectionNote("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to verify family tree");
    } finally {
      setFamilyLoading(false);
    }
  };

  const handleDeleteFamilyTree = async (treeId) => {
    if (!confirm("Are you sure you want to delete this family tree?")) return;
    
    try {
      await adminApi.delete(`/admin/family-trees/${treeId}`);
      toast.success("Family tree deleted");
      fetchData();
    } catch (_error) {
      toast.error("Failed to delete family tree");
    }
  };

  const filteredUsersForFamily = verifiedUsersForFamily.filter(user => {
    const fullName = [user.first_name, user.middle_name, user.last_name].filter(Boolean).join(" ").toLowerCase();
    const searchTerm = familyMemberSearch.toLowerCase();
    return fullName.includes(searchTerm) || user.member_code?.toLowerCase().includes(searchTerm);
  });

  const handleChangeCredentials = async () => {
    if (!credentialsData.current_password) {
      toast.error("Please enter your current password");
      return;
    }
    
    if (credentialsData.new_password && credentialsData.new_password !== credentialsData.confirm_password) {
      toast.error("New passwords don't match");
      return;
    }
    
    if (credentialsData.new_password && credentialsData.new_password.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    
    if (!credentialsData.new_username && !credentialsData.new_password) {
      toast.error("Please enter a new username or password");
      return;
    }
    
    setCredentialsLoading(true);
    try {
      const payload = {
        current_password: credentialsData.current_password
      };
      if (credentialsData.new_username) payload.new_username = credentialsData.new_username;
      if (credentialsData.new_password) payload.new_password = credentialsData.new_password;
      
      await adminApi.put("/admin/auth/change-credentials", payload);
      toast.success("Credentials updated successfully! Please login again.");
      setShowCredentialsDialog(false);
      setCredentialsData({ current_password: "", new_username: "", new_password: "", confirm_password: "" });
      
      // Logout and redirect to login
      localStorage.removeItem("adminToken");
      window.location.href = "/admin-login";
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update credentials");
    } finally {
      setCredentialsLoading(false);
    }
  };

  const handleDownload = async (format) => {
    setDownloadLoading(true);
    setShowDownloadMenu(false);
    
    try {
      // Get current filter from active tab (users = all, pending, verified, rejected)
      const filter = activeTab === 'users' ? 'all' : activeTab;
      
      const response = await adminApi.get(`/admin/download-users?format=${format}&filter=${filter}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Set filename based on format
      const extension = format === 'excel' ? 'xlsx' : format;
      link.setAttribute('download', `lunawat_users_${filter}.${extension}`);
      
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Downloaded ${filter} users as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error("Failed to download user data");
      console.error(error);
    } finally {
      setDownloadLoading(false);
    }
  };

  // Poll Management Functions
  const handleCreatePoll = async () => {
    if (!newPoll.question.trim()) {
      toast.error("Please enter a question");
      return;
    }
    const validOptions = newPoll.options.filter(o => o.trim());
    if (validOptions.length < 2) {
      toast.error("Please enter at least 2 options");
      return;
    }

    setPollLoading(true);
    try {
      await adminApi.post("/admin/polls", {
        question: newPoll.question,
        options: validOptions
      });
      toast.success("Poll created successfully");
      setShowPollDialog(false);
      setNewPoll({ question: "", options: ["", ""] });
      fetchData();
    } catch (_error) {
      toast.error("Failed to create poll");
    } finally {
      setPollLoading(false);
    }
  };

  const handleUpdatePoll = async (pollId, updates) => {
    try {
      await adminApi.put(`/admin/polls/${pollId}`, updates);
      toast.success("Poll updated");
      fetchData();
    } catch (_error) {
      toast.error("Failed to update poll");
    }
  };

  const handleDeletePoll = async (pollId) => {
    if (!window.confirm("Are you sure you want to delete this poll?")) return;
    
    try {
      await adminApi.delete(`/admin/polls/${pollId}`);
      toast.success("Poll deleted");
      fetchData();
    } catch (_error) {
      toast.error("Failed to delete poll");
    }
  };

  const addPollOption = () => {
    setNewPoll(prev => ({
      ...prev,
      options: [...prev.options, ""]
    }));
  };

  const removePollOption = (index) => {
    if (newPoll.options.length <= 2) {
      toast.error("Poll must have at least 2 options");
      return;
    }
    setNewPoll(prev => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index)
    }));
  };

  const updatePollOption = (index, value) => {
    setNewPoll(prev => ({
      ...prev,
      options: prev.options.map((opt, i) => i === index ? value : opt)
    }));
  };

  // Update functions
  const handleCreateUpdate = async () => {
    if (!newUpdate.title.trim() || !newUpdate.content.trim()) {
      toast.error("Please fill in title and content");
      return;
    }

    setUpdateLoading(true);
    try {
      if (editingUpdate) {
        await adminApi.put(`/admin/updates/${editingUpdate.id}`, {
          title: newUpdate.title,
          content: newUpdate.content,
          images: newUpdate.images
        });
        toast.success("Update edited successfully");
      } else {
        await adminApi.post("/admin/updates", {
          title: newUpdate.title,
          content: newUpdate.content,
          images: newUpdate.images
        });
        toast.success("Update created successfully");
      }
      setShowUpdateDialog(false);
      setNewUpdate({ title: "", content: "", images: [] });
      setImagePreview([]);
      setEditingUpdate(null);
      fetchData();
    } catch (_error) {
      toast.error("Failed to save update");
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleDeleteUpdate = async (updateId) => {
    if (!window.confirm("Are you sure you want to delete this update?")) return;
    
    try {
      await adminApi.delete(`/admin/updates/${updateId}`);
      toast.success("Update deleted");
      fetchData();
    } catch (_error) {
      toast.error("Failed to delete update");
    }
  };

  const handleToggleUpdateVisibility = async (updateId, isActive) => {
    try {
      await adminApi.put(`/admin/updates/${updateId}`, { is_active: isActive });
      toast.success(isActive ? "Update is now visible" : "Update is now hidden");
      fetchData();
    } catch (_error) {
      toast.error("Failed to update visibility");
    }
  };

  const handleViewLikes = async (update) => {
    setLikesLoading(true);
    setSelectedUpdateLikes({ update, likes: [] });
    setShowLikesDialog(true);
    
    try {
      const response = await adminApi.get(`/admin/updates/${update.id}/likes`);
      setSelectedUpdateLikes({ update, likes: response.data.liked_by || [] });
    } catch (_error) {
      toast.error("Failed to load likes");
    } finally {
      setLikesLoading(false);
    }
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + newUpdate.images.length > 10) {
      toast.error("Maximum 10 images allowed");
      return;
    }

    files.forEach(file => {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} is too large. Max 5MB per image.`);
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target.result;
        setNewUpdate(prev => ({
          ...prev,
          images: [...prev.images, base64]
        }));
        setImagePreview(prev => [...prev, base64]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index) => {
    setNewUpdate(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
    setImagePreview(prev => prev.filter((_, i) => i !== index));
  };

  // About page functions
  const updateAboutField = (field, value) => {
    setAboutData(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveAbout = async () => {
    setAboutLoading(true);
    try {
      await adminApi.put("/admin/about", aboutData);
      toast.success("About page updated successfully");
    } catch (_error) {
      toast.error("Failed to update about page");
    } finally {
      setAboutLoading(false);
    }
  };

  const addTeamMember = () => {
    if (!newTeamMember.name.trim()) {
      toast.error("Please enter team member name");
      return;
    }
    setAboutData(prev => ({
      ...prev,
      team_members: [...prev.team_members, { ...newTeamMember }]
    }));
    setNewTeamMember({ name: "", designation: "", photo: "" });
  };

  const removeTeamMember = (index) => {
    setAboutData(prev => ({
      ...prev,
      team_members: prev.team_members.filter((_, i) => i !== index)
    }));
  };

  const handleGalleryImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + (aboutData.gallery_images?.length || 0) > 20) {
      toast.error("Maximum 20 images allowed in gallery");
      return;
    }

    files.forEach(file => {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} is too large. Max 5MB per image.`);
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target.result;
        setAboutData(prev => ({
          ...prev,
          gallery_images: [...(prev.gallery_images || []), base64]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const removeGalleryImage = (index) => {
    setAboutData(prev => ({
      ...prev,
      gallery_images: prev.gallery_images.filter((_, i) => i !== index)
    }));
  };

  const handleTeamMemberPhotoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setNewTeamMember(prev => ({ ...prev, photo: event.target.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleVerify = async (userId, status, note = "") => {
    setActionLoading(true);
    try {
      await adminApi.post("/admin/verify-user", {
        user_id: userId,
        status,
        note
      });
      toast.success(`User ${status === "verified" ? "verified" : "rejected"} successfully`);
      setShowUserDialog(false);
      setSelectedUser(null);
      setRejectionNote("");
      fetchData();
    } catch (_error) {
      toast.error("Failed to update user status");
    } finally {
      setActionLoading(false);
    }
  };

  // Fetch full user details for View dialog
  const handleViewUser = async (user) => {
    try {
      const res = await adminApi.get(`/admin/user/${user.id}`);
      setSelectedUser(res.data);
      setShowUserDialog(true);
    } catch (_error) {
      // Fallback to list data if detail fetch fails
      setSelectedUser(user);
      setShowUserDialog(true);
    }
  };


  // Keep rejected user (reset to pending for re-verification)
  const handleKeepUser = async (user) => {
    setActionLoading(true);
    try {
      await adminApi.post("/admin/verify-user", {
        user_id: user.id,
        status: "pending",
        note: "Profile kept for re-verification"
      });
      toast.success("User profile kept. Status set to pending for re-verification.");
      fetchData();
    } catch (_error) {
      toast.error("Failed to update user status");
    } finally {
      setActionLoading(false);
    }
  };

  // Delete user from database
  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    
    setDeleteLoading(true);
    try {
      await adminApi.delete(`/admin/users/${userToDelete.id}`);
      toast.success("User profile deleted permanently");
      setShowDeleteUserDialog(false);
      setUserToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleEditUser = async (user) => {
    try {
      // Fetch full user data and prefixes in parallel
      const [userRes, prefixRes] = await Promise.all([
        adminApi.get(`/admin/user/${user.id}`),
        prefixes.length === 0 ? adminApi.get("/admin/prefixes") : Promise.resolve(null)
      ]);
      const fullUser = userRes.data;
      if (prefixRes) setPrefixes(prefixRes.data);
      setEditUserData({
        id: fullUser.id,
        phone: fullUser.phone || "",
        prefix: fullUser.prefix || "",
        first_name: fullUser.first_name || "",
        middle_name: fullUser.middle_name || "",
        last_name: fullUser.last_name || "LUNAWAT",
        username: fullUser.username || "",
        date_of_birth: fullUser.date_of_birth || "",
        gender: fullUser.gender || "",
        blood_group: fullUser.blood_group || "",
        native_place: fullUser.native_place || "",
        marital_status: fullUser.marital_status || "",
        spouse_name: fullUser.spouse_name || "",
        date_of_marriage: fullUser.date_of_marriage || "",
        number_of_children: fullUser.number_of_children || 0,
        children: fullUser.children || [],
        father_name: fullUser.father_name || "",
        mother_name: fullUser.mother_name || "",
        address_line1: fullUser.address_line1 || "",
        address_line2: fullUser.address_line2 || "",
        city: fullUser.city || "",
        state: fullUser.state || "",
        pincode: fullUser.pincode || "",
        country: fullUser.country || "India",
        is_overseas: fullUser.is_overseas || false,
        occupation: fullUser.occupation || "",
        company_name: fullUser.company_name || "",
        designation: fullUser.designation || "",
        work_address: fullUser.work_address || "",
        bio: fullUser.bio || "",
        verification_status: fullUser.verification_status || "pending"
      });
      setShowEditDialog(true);
      setShowUserDialog(false);
    } catch (_error) {
      // Fallback to list data
      setEditUserData({
        id: user.id,
        phone: user.phone || "",
        prefix: user.prefix || "",
        first_name: user.first_name || "",
        middle_name: user.middle_name || "",
        last_name: user.last_name || "LUNAWAT",
        username: user.username || "",
        date_of_birth: user.date_of_birth || "",
        gender: user.gender || "",
        blood_group: user.blood_group || "",
        native_place: user.native_place || "",
        marital_status: user.marital_status || "",
        spouse_name: user.spouse_name || "",
        date_of_marriage: user.date_of_marriage || "",
        number_of_children: user.number_of_children || 0,
        children: user.children || [],
        father_name: user.father_name || "",
        mother_name: user.mother_name || "",
        address_line1: user.address_line1 || "",
        address_line2: user.address_line2 || "",
        city: user.city || "",
        state: user.state || "",
        pincode: user.pincode || "",
        country: user.country || "India",
        is_overseas: user.is_overseas || false,
        occupation: user.occupation || "",
        company_name: user.company_name || "",
        designation: user.designation || "",
        work_address: user.work_address || "",
        bio: user.bio || "",
        verification_status: user.verification_status || "pending"
      });
      setShowEditDialog(true);
      setShowUserDialog(false);
    }
  };

  const handleSaveUser = async () => {
    if (!editUserData) return;
    
    setActionLoading(true);
    try {
      await adminApi.put(`/admin/users/${editUserData.id}`, editUserData);
      toast.success("User updated successfully");
      setShowEditDialog(false);
      setEditUserData(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update user");
    } finally {
      setActionLoading(false);
    }
  };

  const updateEditField = (field, value) => {
    setEditUserData(prev => ({ ...prev, [field]: value }));
  };

  // Update a specific child's field in edit mode
  const updateEditChildField = (index, field, value) => {
    setEditUserData(prev => {
      const newChildren = [...(prev.children || [])];
      newChildren[index] = { ...newChildren[index], [field]: value };
      return { ...prev, children: newChildren };
    });
  };

  // Handle number of children change in edit mode
  const handleEditChildrenCountChange = (count) => {
    const numCount = parseInt(count);
    setEditUserData(prev => {
      const existingChildren = prev.children || [];
      const newChildren = [];
      for (let i = 0; i < numCount; i++) {
        newChildren.push(existingChildren[i] || {
          name: "",
          gender: "",
          age: "",
          education: "",
          marital_status: "",
          photo: ""
        });
      }
      return { ...prev, number_of_children: numCount, children: newChildren };
    });
  };

  const handleCreateAdmin = async () => {
    if (!newAdmin.username || !newAdmin.password || !newAdmin.name) {
      toast.error("All fields are required");
      return;
    }
    
    setActionLoading(true);
    try {
      await adminApi.post("/admin/admins", newAdmin);
      toast.success("Admin created successfully");
      setShowAdminDialog(false);
      setNewAdmin({ username: "", password: "", name: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create admin");
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleAdminStatus = async (adminId) => {
    try {
      await adminApi.put(`/admin/admins/${adminId}/toggle-status`);
      toast.success("Admin status updated");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update status");
    }
  };

  const handleDeleteAdmin = async (adminId) => {
    if (!confirm("Are you sure you want to delete this admin?")) return;
    
    try {
      await adminApi.delete(`/admin/admins/${adminId}`);
      toast.success("Admin deleted");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete admin");
    }
  };

  const handleCreatePrefix = async () => {
    if (!newPrefix.value || !newPrefix.label) {
      toast.error("Both value and label are required");
      return;
    }
    
    setActionLoading(true);
    try {
      await adminApi.post("/admin/prefixes", newPrefix);
      toast.success("Prefix created successfully");
      setShowPrefixDialog(false);
      setNewPrefix({ value: "", label: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create prefix");
    } finally {
      setActionLoading(false);
    }
  };

  const handleTogglePrefixStatus = async (prefixId, currentStatus) => {
    try {
      await adminApi.put(`/admin/prefixes/${prefixId}`, { is_active: !currentStatus });
      toast.success("Prefix status updated");
      fetchData();
    } catch (_error) {
      toast.error("Failed to update prefix");
    }
  };

  const handleDeletePrefix = async (prefixId) => {
    if (!confirm("Are you sure you want to delete this prefix?")) return;
    
    try {
      await adminApi.delete(`/admin/prefixes/${prefixId}`);
      toast.success("Prefix deleted");
      fetchData();
    } catch (_error) {
      toast.error("Failed to delete prefix");
    }
  };

  const getInitials = (u) => {
    if (u.first_name) {
      return `${u.first_name[0]}${(u.last_name || "L")[0]}`.toUpperCase();
    }
    return u.phone?.slice(-2) || "??";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status, isComplete) => {
    switch (status) {
      case "verified":
        return <Badge className="bg-green-100 text-green-800 hover:bg-green-100"><CheckCircle className="w-3 h-3 mr-1" /> Verified</Badge>;
      case "rejected":
        return <Badge className="bg-red-100 text-red-800 hover:bg-red-100"><XCircle className="w-3 h-3 mr-1" /> Rejected</Badge>;
      case "pending":
        return <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100"><Clock className="w-3 h-3 mr-1" /> Pending</Badge>;
      default:
        // Show different badge for incomplete vs awaiting verification
        if (isComplete) {
          return <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100"><Clock className="w-3 h-3 mr-1" /> Awaiting</Badge>;
        }
        return <Badge className="bg-gray-100 text-gray-800 hover:bg-gray-100"><Clock className="w-3 h-3 mr-1" /> Incomplete</Badge>;
    }
  };

  // Helper function to check if user needs verification
  const needsVerification = (u) => {
    // User needs verification if:
    // 1. Has pending status, OR
    // 2. Has completed profile but status is not verified/rejected (null, undefined, or empty)
    if (u.verification_status === "pending") return true;
    if (u.is_profile_complete && u.verification_status !== "verified" && u.verification_status !== "rejected") return true;
    return false;
  };

  // Calculate stats with fallbacks
  const stats = {
    total: totalUsers || allUsers.length,
    pending: pendingTotal || pendingUsers.length,
    verified: verifiedTotal || allUsers.filter(u => u.verification_status === "verified").length,
    rejected: rejectedTotal || allUsers.filter(u => u.verification_status === "rejected").length
  };

  // eslint-disable-next-line react/no-unstable-nested-components
  const UserCard = ({ u }) => (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <Avatar className="w-12 h-12">
            <AvatarImage src={u.avatar} />
            <AvatarFallback className="bg-primary/10 text-primary font-semibold">
              {getInitials(u)}
            </AvatarFallback>
          </Avatar>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold truncate">
                {u.prefix && <span className="text-muted-foreground">{u.prefix}. </span>}
                {u.first_name} {u.last_name || "LUNAWAT"}
              </h3>
              {getStatusBadge(u.verification_status, u.is_profile_complete)}
              {/* Member Code Badge - Show for verified users */}
              {u.verification_status === 'verified' && u.member_code && (
                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                  {u.member_code}
                </Badge>
              )}
            </div>
            
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Phone className="w-3 h-3" /> {u.phone}
              </span>
              {u.city && (
                <span className="flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> {u.city}, {u.state}
                </span>
              )}
              {u.occupation && (
                <span className="flex items-center gap-1 capitalize">
                  <Briefcase className="w-3 h-3" /> {u.occupation}
                </span>
              )}
            </div>
          </div>

          <div className="flex gap-2 flex-wrap justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleViewUser(u)}
              data-testid={`view-user-${u.id}`}
            >
              <Eye className="w-4 h-4 mr-1" /> View
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleEditUser(u)}
              data-testid={`edit-user-${u.id}`}
            >
              <Edit className="w-4 h-4 mr-1" /> Edit
            </Button>
            
            {/* Show Verify/Reject for users who need verification */}
            {needsVerification(u) && (
              <>
                {/* Call Button */}
                <Button
                  variant="outline"
                  size="sm"
                  className="bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200"
                  onClick={() => window.open(`tel:${u.phone}`, '_self')}
                  data-testid={`call-btn-${u.id}`}
                >
                  <PhoneCall className="w-4 h-4 mr-1" /> Call
                </Button>
                
                <Button
                  variant="default"
                  size="sm"
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => handleVerify(u.id, "verified")}
                  disabled={actionLoading}
                  data-testid={`verify-btn-${u.id}`}
                >
                  <CheckCircle className="w-4 h-4 mr-1" /> Verify
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => handleViewUser(u)}
                  disabled={actionLoading}
                  data-testid={`reject-btn-${u.id}`}
                >
                  <XCircle className="w-4 h-4 mr-1" /> Reject
                </Button>
              </>
            )}

            {/* Show Keep/Remove for rejected users */}
            {u.verification_status === "rejected" && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  className="bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
                  onClick={() => handleKeepUser(u)}
                  disabled={actionLoading}
                  data-testid={`keep-btn-${u.id}`}
                >
                  <CheckCircle className="w-4 h-4 mr-1" /> Keep
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => {
                    setUserToDelete(u);
                    setShowDeleteUserDialog(true);
                  }}
                  disabled={actionLoading}
                  data-testid={`remove-btn-${u.id}`}
                >
                  <Trash2 className="w-4 h-4 mr-1" /> Remove
                </Button>
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-background" data-testid="admin-panel-page">
      <Toaster position="top-center" richColors />
      
      {/* Header */}
      <header className="sticky top-0 z-50 h-16 border-b border-border bg-background/95 backdrop-blur">
        <div className="container mx-auto h-full px-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
              <Shield className="w-5 h-5 text-primary-foreground" />
            </div>
            <div>
              <h1 className="font-bold">Admin Panel</h1>
              <p className="text-xs text-muted-foreground">LUNAWAT FOUNDATION Administration</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium">{admin?.name}</p>
              <p className="text-xs text-muted-foreground capitalize">{admin?.role} Admin</p>
            </div>
            {admin?.role === "master" && (
              <Button variant="outline" size="sm" onClick={() => setShowCredentialsDialog(true)} data-testid="change-credentials-btn">
                <Key className="w-4 h-4 mr-2" />
                Change Credentials
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={handleLogout} data-testid="admin-logout-btn">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto py-6 px-4">
        {/* Stats Cards - Clickable */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card 
            className={`cursor-pointer transition-all hover:shadow-md ${activeTab === 'users' ? 'ring-2 ring-blue-500' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total}</p>
                <p className="text-sm text-muted-foreground">Total Users</p>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className={`cursor-pointer transition-all hover:shadow-md ${activeTab === 'pending' ? 'ring-2 ring-amber-500' : ''}`}
            onClick={() => setActiveTab('pending')}
          >
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.pending}</p>
                <p className="text-sm text-muted-foreground">Pending</p>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className={`cursor-pointer transition-all hover:shadow-md ${activeTab === 'verified' ? 'ring-2 ring-green-500' : ''}`}
            onClick={() => setActiveTab('verified')}
          >
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30">
                <UserCheck className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.verified}</p>
                <p className="text-sm text-muted-foreground">Verified</p>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className={`cursor-pointer transition-all hover:shadow-md ${activeTab === 'rejected' ? 'ring-2 ring-red-500' : ''}`}
            onClick={() => setActiveTab('rejected')}
          >
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/30">
                <UserX className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.rejected}</p>
                <p className="text-sm text-muted-foreground">Rejected</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search Box */}
        <div className="relative mb-4" data-testid="admin-search-box">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, phone, member code, or city..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              className="pl-10 pr-10"
              data-testid="admin-search-input"
            />
            {searchQuery && (
              <button
                onClick={clearSearch}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                data-testid="admin-search-clear"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          {isSearching && (
            <div className="absolute right-12 top-1/2 -translate-y-1/2">
              <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
            </div>
          )}
        </div>

        {/* Search Results */}
        {searchQuery.trim() ? (
          <div className="space-y-3 mb-4">
            <h2 className="text-lg font-semibold">
              Search Results ({searchResults.length})
              <span className="text-sm font-normal text-muted-foreground ml-2">for "{searchQuery}"</span>
            </h2>
            {searchResults.length === 0 && !isSearching ? (
              <Card>
                <CardContent className="p-8 text-center text-muted-foreground">
                  <Search className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No users found matching "{searchQuery}"</p>
                </CardContent>
              </Card>
            ) : (
              searchResults.map(u => <UserCard key={u.id} u={u} />)
            )}
          </div>
        ) : (

        <>
        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          {/* User Management Tabs */}
          <div className="mb-2">
            <p className="text-xs text-muted-foreground mb-2 uppercase font-medium">User Management</p>
            <TabsList className="grid w-full grid-cols-4 h-auto">
              <TabsTrigger value="users" data-testid="tab-users" className="py-2.5 px-3 text-sm">
                <Users className="w-4 h-4 mr-1.5" />
                All ({stats.total})
              </TabsTrigger>
              <TabsTrigger value="pending" data-testid="tab-pending" className="py-2.5 px-3 text-sm">
                <Clock className="w-4 h-4 mr-1.5" />
                Pending ({stats.pending})
              </TabsTrigger>
              <TabsTrigger value="verified" data-testid="tab-verified" className="py-2.5 px-3 text-sm">
                <UserCheck className="w-4 h-4 mr-1.5" />
                Verified ({stats.verified})
              </TabsTrigger>
              <TabsTrigger value="rejected" data-testid="tab-rejected" className="py-2.5 px-3 text-sm">
                <UserX className="w-4 h-4 mr-1.5" />
                Rejected ({stats.rejected})
              </TabsTrigger>
            </TabsList>
          </div>
          
          {/* Settings Tabs */}
          <div className="mb-4">
            <p className="text-xs text-muted-foreground mb-2 uppercase font-medium">Settings</p>
            <TabsList className="grid w-full grid-cols-5 max-w-2xl h-auto">
              <TabsTrigger value="admins" data-testid="tab-admins" className="py-2.5 px-3 text-sm">
                <Shield className="w-4 h-4 mr-1.5" />
                Admins
              </TabsTrigger>
              <TabsTrigger value="prefixes" data-testid="tab-prefixes" className="py-2.5 px-3 text-sm">
                <Tag className="w-4 h-4 mr-1.5" />
                Prefixes
              </TabsTrigger>
              <TabsTrigger value="polls" data-testid="tab-polls" className="py-2.5 px-3 text-sm">
                <Vote className="w-4 h-4 mr-1.5" />
                Polls
              </TabsTrigger>
              <TabsTrigger value="updates" data-testid="tab-updates" className="py-2.5 px-3 text-sm">
                <Bell className="w-4 h-4 mr-1.5" />
                Updates
              </TabsTrigger>
              <TabsTrigger value="about" data-testid="tab-about" className="py-2.5 px-3 text-sm">
                <Building2 className="w-4 h-4 mr-1.5" />
                About
              </TabsTrigger>
              <TabsTrigger value="family-tree" data-testid="tab-family-tree" className="py-2.5 px-3 text-sm">
                <GitBranch className="w-4 h-4 mr-1.5" />
                Family Tree
              </TabsTrigger>
              <TabsTrigger value="popup" data-testid="tab-popup" className="py-2.5 px-3 text-sm">
                <ImageIcon className="w-4 h-4 mr-1.5" />
                Popup
              </TabsTrigger>
              <TabsTrigger value="travel-forms" data-testid="tab-travel-forms" className="py-2.5 px-3 text-sm">
                <Plane className="w-4 h-4 mr-1.5" />
                Travel Forms
              </TabsTrigger>
              <TabsTrigger value="kuldevi-forms" data-testid="tab-kuldevi-forms" className="py-2.5 px-3 text-sm">
                <BookOpen className="w-4 h-4 mr-1.5" />
                Kuldevi Forms
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-3">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">All Users ({stats.total})</h2>
              <div className="flex gap-2">
                {/* Download Button with Dropdown */}
                <div className="relative">
                  <Button 
                    variant="outline" 
                    onClick={() => setShowDownloadMenu(!showDownloadMenu)}
                    disabled={downloadLoading}
                    data-testid="download-btn"
                  >
                    {downloadLoading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4 mr-2" />
                    )}
                    Download
                  </Button>
                  {showDownloadMenu && (
                    <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg border z-50">
                      <div className="py-1">
                        <button
                          onClick={() => handleDownload('csv')}
                          className="flex items-center gap-2 w-full px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                          data-testid="download-csv"
                        >
                          <File className="w-4 h-4" />
                          Download CSV
                        </button>
                        <button
                          onClick={() => handleDownload('excel')}
                          className="flex items-center gap-2 w-full px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                          data-testid="download-excel"
                        >
                          <FileSpreadsheet className="w-4 h-4" />
                          Download Excel
                        </button>
                        <button
                          onClick={() => handleDownload('pdf')}
                          className="flex items-center gap-2 w-full px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                          data-testid="download-pdf"
                        >
                          <FileText className="w-4 h-4" />
                          Download PDF
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                <Button variant="outline" onClick={fetchData} disabled={loading}>
                  <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </div>
            
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : allUsers.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center text-muted-foreground">
                  <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No users registered yet</p>
                </CardContent>
              </Card>
            ) : (
              <>
                {allUsers.map(u => <UserCard key={u.id} u={u} />)}
                
                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-4 pt-4 border-t mt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage <= 1 || loading}
                      data-testid="pagination-prev"
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Previous
                    </Button>
                    
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">
                        Page {currentPage} of {totalPages}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        ({totalUsers} total users)
                      </span>
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage >= totalPages || loading}
                      data-testid="pagination-next"
                    >
                      Next
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                )}
              </>
            )}
          </TabsContent>

          {/* Pending Tab */}
          <TabsContent value="pending" className="space-y-3">
            <h2 className="text-lg font-semibold mb-4">Pending Verifications ({stats.pending})</h2>
            
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : pendingUsers.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center text-muted-foreground">
                  <Clock className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No pending verifications</p>
                </CardContent>
              </Card>
            ) : (
              <>
                {pendingUsers.map(u => <UserCard key={u.id} u={u} />)}
                
                {/* Pending Pagination Controls */}
                {pendingTotalPages > 1 && (
                  <div className="flex items-center justify-center gap-4 pt-4 border-t mt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePendingPageChange(pendingPage - 1)}
                      disabled={pendingPage <= 1 || loading}
                      data-testid="pending-pagination-prev"
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Previous
                    </Button>
                    
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">
                        Page {pendingPage} of {pendingTotalPages}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        ({pendingTotal} pending)
                      </span>
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePendingPageChange(pendingPage + 1)}
                      disabled={pendingPage >= pendingTotalPages || loading}
                      data-testid="pending-pagination-next"
                    >
                      Next
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                )}
              </>
            )}
          </TabsContent>

          {/* Verified Tab */}
          <TabsContent value="verified" className="space-y-3">
            <h2 className="text-lg font-semibold mb-4">Verified Users ({verifiedTotal})</h2>
            
            {verifiedUsers.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center text-muted-foreground">
                  <UserCheck className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No verified users</p>
                </CardContent>
              </Card>
            ) : (
              <>
                {verifiedUsers.map(u => <UserCard key={u.id} u={u} />)}
                {verifiedTotalPages > 1 && (
                  <div className="flex justify-center items-center gap-2 mt-4">
                    <Button variant="outline" size="sm" disabled={verifiedPage <= 1} onClick={() => fetchVerifiedUsers(verifiedPage - 1)}>Previous</Button>
                    <span className="text-sm text-muted-foreground">Page {verifiedPage} of {verifiedTotalPages}</span>
                    <Button variant="outline" size="sm" disabled={verifiedPage >= verifiedTotalPages} onClick={() => fetchVerifiedUsers(verifiedPage + 1)}>Next</Button>
                  </div>
                )}
              </>
            )}
          </TabsContent>

          {/* Rejected Tab */}
          <TabsContent value="rejected" className="space-y-3">
            <h2 className="text-lg font-semibold mb-4">Rejected Users ({rejectedTotal})</h2>
            
            {rejectedUsers.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center text-muted-foreground">
                  <UserX className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No rejected users</p>
                </CardContent>
              </Card>
            ) : (
              <>
                {rejectedUsers.map(u => <UserCard key={u.id} u={u} />)}
                {rejectedTotalPages > 1 && (
                  <div className="flex justify-center items-center gap-2 mt-4">
                    <Button variant="outline" size="sm" disabled={rejectedPage <= 1} onClick={() => fetchRejectedUsers(rejectedPage - 1)}>Previous</Button>
                    <span className="text-sm text-muted-foreground">Page {rejectedPage} of {rejectedTotalPages}</span>
                    <Button variant="outline" size="sm" disabled={rejectedPage >= rejectedTotalPages} onClick={() => fetchRejectedUsers(rejectedPage + 1)}>Next</Button>
                  </div>
                )}
              </>
            )}
          </TabsContent>

          {/* Admins Tab */}
          <TabsContent value="admins" className="space-y-3">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Admin Users</h2>
              {admin?.role === "master" && (
                <Button onClick={() => setShowAdminDialog(true)} data-testid="create-admin-btn">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Create Admin
                </Button>
              )}
            </div>
            
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : (
              <div className="space-y-3">
                {admins.map(a => (
                  <Card key={a.id}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${a.role === 'master' ? 'bg-amber-100' : 'bg-primary/10'}`}>
                            <Shield className={`w-5 h-5 ${a.role === 'master' ? 'text-amber-600' : 'text-primary'}`} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold">{a.name}</h3>
                              <Badge variant={a.role === 'master' ? 'default' : 'secondary'} className="capitalize">
                                {a.role}
                              </Badge>
                              {!a.is_active && (
                                <Badge variant="destructive">Inactive</Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">@{a.username}</p>
                          </div>
                        </div>
                        
                        {admin?.role === "master" && a.role !== "master" && (
                          <div className="flex items-center gap-2">
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-muted-foreground">Active</span>
                              <Switch
                                checked={a.is_active}
                                onCheckedChange={() => handleToggleAdminStatus(a.id)}
                                data-testid={`toggle-admin-${a.id}`}
                              />
                            </div>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleDeleteAdmin(a.id)}
                              data-testid={`delete-admin-${a.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Prefixes Tab */}
          <TabsContent value="prefixes" className="space-y-3">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Name Prefixes</h2>
              <Button onClick={() => setShowPrefixDialog(true)} data-testid="create-prefix-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Prefix
              </Button>
            </div>
            
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {prefixes.map(p => (
                  <Card key={p.id} className={!p.is_active ? 'opacity-50' : ''}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold">{p.label}</p>
                          <p className="text-sm text-muted-foreground">Value: {p.value}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={p.is_active}
                            onCheckedChange={() => handleTogglePrefixStatus(p.id, p.is_active)}
                            data-testid={`toggle-prefix-${p.id}`}
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeletePrefix(p.id)}
                            data-testid={`delete-prefix-${p.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Polls Tab */}
          <TabsContent value="polls" className="space-y-3">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Manage Polls ({polls.length})</h2>
              <Button onClick={() => setShowPollDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Create Poll
              </Button>
            </div>
            
            {polls.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="p-8 text-center">
                  <BarChart3 className="w-12 h-12 mx-auto mb-3 text-muted-foreground/30" />
                  <p className="text-muted-foreground">No polls created yet</p>
                  <Button variant="outline" className="mt-4" onClick={() => setShowPollDialog(true)}>
                    Create First Poll
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {polls.map((poll) => (
                  <Card key={poll.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-semibold">{poll.question}</h3>
                            {!poll.is_active && (
                              <Badge variant="secondary">Inactive</Badge>
                            )}
                          </div>
                          
                          {/* Options with vote counts */}
                          <div className="space-y-2 mt-3">
                            {poll.options.map((option, index) => {
                              const votes = poll.option_votes?.[index] || 0;
                              const percentage = poll.total_votes > 0 
                                ? Math.round((votes / poll.total_votes) * 100) 
                                : 0;
                              return (
                                <div key={`opt-${option}-${index}`} className="relative">
                                  <div 
                                    className="absolute inset-0 bg-primary/10 rounded"
                                    style={{ width: `${percentage}%` }}
                                  />
                                  <div className="relative flex justify-between items-center p-2 text-sm">
                                    <span>{option}</span>
                                    <span className="text-muted-foreground">{votes} ({percentage}%)</span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          
                          <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
                            <span>Total: {poll.total_votes} votes</span>
                            <span>Created: {new Date(poll.created_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                        
                        <div className="flex flex-col gap-2">
                          {/* Show Results Toggle */}
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">Show Results:</span>
                            <Switch
                              checked={poll.show_results}
                              onCheckedChange={(checked) => handleUpdatePoll(poll.id, { show_results: checked })}
                            />
                          </div>
                          
                          {/* Active Toggle */}
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">Active:</span>
                            <Switch
                              checked={poll.is_active}
                              onCheckedChange={(checked) => handleUpdatePoll(poll.id, { is_active: checked })}
                            />
                          </div>
                          
                          {/* Delete Button */}
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                            onClick={() => handleDeletePoll(poll.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Updates Tab */}
          <TabsContent value="updates" className="space-y-3">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Updates ({updates.length})</h2>
              <Button onClick={() => { setEditingUpdate(null); setNewUpdate({ title: "", content: "", images: [] }); setImagePreview([]); setShowUpdateDialog(true); }}>
                <Plus className="w-4 h-4 mr-2" />
                Create Update
              </Button>
            </div>

            {updates.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <Bell className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                  <h3 className="font-semibold text-lg mb-2">No Updates Yet</h3>
                  <p className="text-muted-foreground text-sm mb-4">
                    Create updates to share announcements with users.
                  </p>
                  <Button onClick={() => { setEditingUpdate(null); setNewUpdate({ title: "", content: "", images: [] }); setImagePreview([]); setShowUpdateDialog(true); }}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create First Update
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {updates.map((update) => (
                  <Card key={update.id}>
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-lg flex items-center gap-2">
                            {update.title}
                            {!update.is_active && (
                              <Badge variant="secondary">Hidden</Badge>
                            )}
                          </CardTitle>
                          <p className="text-xs text-muted-foreground mt-1">
                            {new Date(update.created_at).toLocaleDateString('en-IN', {
                              day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                            })}
                            {update.images?.length > 0 && ` • ${update.images.length} image(s)`}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setEditingUpdate(update);
                              setNewUpdate({ title: update.title, content: update.content, images: update.images || [] });
                              setImagePreview(update.images || []);
                              setShowUpdateDialog(true);
                            }}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-600"
                            onClick={() => handleDeleteUpdate(update.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div 
                        className="prose prose-sm max-w-none dark:prose-invert line-clamp-3"
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(update.content) }}
                      />
                      {update.images?.length > 0 && (
                        <div className="flex gap-2 mt-3 overflow-x-auto">
                          {update.images.slice(0, 4).map((img, idx) => (
                            <img key={`upd-img-${idx}`} src={img} alt="" className="w-16 h-16 object-cover rounded" />
                          ))}
                          {update.images.length > 4 && (
                            <div className="w-16 h-16 bg-muted rounded flex items-center justify-center text-sm">
                              +{update.images.length - 4}
                            </div>
                          )}
                        </div>
                      )}
                      <div className="flex items-center gap-4 mt-3">
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={update.is_active}
                            onCheckedChange={(checked) => handleToggleUpdateVisibility(update.id, checked)}
                          />
                          <span className="text-sm text-muted-foreground">
                            {update.is_active ? "Visible" : "Hidden"}
                          </span>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewLikes(update)}
                          className="gap-2"
                          data-testid={`view-likes-${update.id}`}
                        >
                          <Heart className="w-4 h-4" />
                          {update.likes?.length || 0} Likes
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* About Tab */}
          <TabsContent value="about" className="space-y-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">About Page Settings</h2>
              <Button onClick={handleSaveAbout} disabled={aboutLoading} data-testid="save-about-btn">
                {aboutLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Save Changes
              </Button>
            </div>

            {/* Organization Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Organization Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Organization Name</label>
                  <Input
                    value={aboutData.organization_name || ''}
                    onChange={(e) => updateAboutField('organization_name', e.target.value)}
                    placeholder="LUNAWAT FOUNDATION"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Description</label>
                  <Textarea
                    value={aboutData.description || ''}
                    onChange={(e) => updateAboutField('description', e.target.value)}
                    placeholder="Brief description of your organization..."
                    rows={3}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">History</label>
                  <Textarea
                    value={aboutData.history || ''}
                    onChange={(e) => updateAboutField('history', e.target.value)}
                    placeholder="Organization history..."
                    rows={4}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium flex items-center gap-1">
                      <Target className="w-4 h-4" /> Mission
                    </label>
                    <Textarea
                      value={aboutData.mission || ''}
                      onChange={(e) => updateAboutField('mission', e.target.value)}
                      placeholder="Our mission..."
                      rows={3}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium flex items-center gap-1">
                      <Eye className="w-4 h-4" /> Vision
                    </label>
                    <Textarea
                      value={aboutData.vision || ''}
                      onChange={(e) => updateAboutField('vision', e.target.value)}
                      placeholder="Our vision..."
                      rows={3}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Contact Information */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Phone className="w-5 h-5" />
                  Contact Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium flex items-center gap-1">
                    <MapPin className="w-4 h-4" /> Address
                  </label>
                  <Textarea
                    value={aboutData.contact_address || ''}
                    onChange={(e) => updateAboutField('contact_address', e.target.value)}
                    placeholder="Organization address..."
                    rows={2}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium flex items-center gap-1">
                      <Phone className="w-4 h-4" /> Phone
                    </label>
                    <Input
                      value={aboutData.contact_phone || ''}
                      onChange={(e) => updateAboutField('contact_phone', e.target.value)}
                      placeholder="+91 XXXXXXXXXX"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium flex items-center gap-1">
                      <Mail className="w-4 h-4" /> Email
                    </label>
                    <Input
                      type="email"
                      value={aboutData.contact_email || ''}
                      onChange={(e) => updateAboutField('contact_email', e.target.value)}
                      placeholder="contact@example.com"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Social Media Links */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Globe className="w-5 h-5" />
                  Social Media Links
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium flex items-center gap-1">
                      <Facebook className="w-4 h-4 text-blue-600" /> Facebook
                    </label>
                    <Input
                      value={aboutData.facebook_url || ''}
                      onChange={(e) => updateAboutField('facebook_url', e.target.value)}
                      placeholder="https://facebook.com/..."
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium flex items-center gap-1">
                      <Instagram className="w-4 h-4 text-pink-600" /> Instagram
                    </label>
                    <Input
                      value={aboutData.instagram_url || ''}
                      onChange={(e) => updateAboutField('instagram_url', e.target.value)}
                      placeholder="https://instagram.com/..."
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium flex items-center gap-1">
                      <Twitter className="w-4 h-4 text-sky-500" /> Twitter
                    </label>
                    <Input
                      value={aboutData.twitter_url || ''}
                      onChange={(e) => updateAboutField('twitter_url', e.target.value)}
                      placeholder="https://twitter.com/..."
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium flex items-center gap-1">
                      <Linkedin className="w-4 h-4 text-blue-700" /> LinkedIn
                    </label>
                    <Input
                      value={aboutData.linkedin_url || ''}
                      onChange={(e) => updateAboutField('linkedin_url', e.target.value)}
                      placeholder="https://linkedin.com/..."
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium flex items-center gap-1">
                      <Youtube className="w-4 h-4 text-red-600" /> YouTube
                    </label>
                    <Input
                      value={aboutData.youtube_url || ''}
                      onChange={(e) => updateAboutField('youtube_url', e.target.value)}
                      placeholder="https://youtube.com/..."
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium flex items-center gap-1">
                      <Globe className="w-4 h-4 text-teal-600" /> Website
                    </label>
                    <Input
                      value={aboutData.website_url || ''}
                      onChange={(e) => updateAboutField('website_url', e.target.value)}
                      placeholder="https://example.com"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Team/Committee Members */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Team / Committee Members
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Add New Team Member */}
                <div className="p-4 border rounded-lg bg-muted/30 space-y-3">
                  <h4 className="text-sm font-medium">Add New Member</h4>
                  <div className="flex gap-3 items-end">
                    <div className="flex-shrink-0">
                      <Avatar className="w-14 h-14">
                        <AvatarImage src={newTeamMember.photo} />
                        <AvatarFallback className="bg-primary/10 text-primary">
                          {newTeamMember.name?.[0] || "?"}
                        </AvatarFallback>
                      </Avatar>
                      <label className="block mt-1">
                        <span className="text-xs text-primary cursor-pointer hover:underline">Upload Photo</span>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleTeamMemberPhotoUpload}
                          className="hidden"
                        />
                      </label>
                    </div>
                    <div className="flex-1 space-y-2">
                      <Input
                        placeholder="Name"
                        value={newTeamMember.name}
                        onChange={(e) => setNewTeamMember(prev => ({ ...prev, name: e.target.value }))}
                      />
                      <Input
                        placeholder="Designation (e.g., President, Secretary)"
                        value={newTeamMember.designation}
                        onChange={(e) => setNewTeamMember(prev => ({ ...prev, designation: e.target.value }))}
                      />
                    </div>
                    <Button onClick={addTeamMember} size="sm">
                      <Plus className="w-4 h-4 mr-1" /> Add
                    </Button>
                  </div>
                </div>

                {/* Existing Team Members */}
                {aboutData.team_members && aboutData.team_members.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {aboutData.team_members.map((member, index) => (
                      <div key={`team-${member.name || index}`} className="relative p-3 border rounded-lg text-center group">
                        <button
                          onClick={() => removeTeamMember(index)}
                          className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                        <Avatar className="w-12 h-12 mx-auto mb-2">
                          <AvatarImage src={member.photo} />
                          <AvatarFallback className="bg-primary/10 text-primary">
                            {member.name?.[0] || "?"}
                          </AvatarFallback>
                        </Avatar>
                        <p className="font-medium text-sm truncate">{member.name}</p>
                        {member.designation && (
                          <p className="text-xs text-muted-foreground truncate">{member.designation}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Gallery Images */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <ImageIcon className="w-5 h-5" />
                  Gallery Images
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2">
                  <label className="cursor-pointer">
                    <span className="inline-flex items-center px-4 py-2 border rounded-md hover:bg-muted transition-colors">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Images
                    </span>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleGalleryImageUpload}
                      className="hidden"
                    />
                  </label>
                  <span className="text-sm text-muted-foreground">
                    {aboutData.gallery_images?.length || 0} / 20 images
                  </span>
                </div>

                {aboutData.gallery_images && aboutData.gallery_images.length > 0 && (
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
                    {aboutData.gallery_images.map((img, index) => (
                      <div key={`gal-${index}-${img.slice(-15)}`} className="relative aspect-square group">
                        <img
                          src={img}
                          alt={`Gallery ${index + 1}`}
                          className="w-full h-full object-cover rounded-lg"
                        />
                        <button
                          onClick={() => removeGalleryImage(index)}
                          className="absolute top-1 right-1 w-6 h-6 rounded-full bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Family Tree Tab */}
          <TabsContent value="family-tree" className="space-y-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className="text-lg font-semibold">Family Trees</h2>
                <p className="text-sm text-muted-foreground">
                  {familyTrees.filter(f => f.status === "verified").length} verified families
                </p>
              </div>
              <Button onClick={() => setShowCreateFamilyDialog(true)} data-testid="create-family-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Family Tree
              </Button>
            </div>

            {/* Family Trees List */}
            <div className="grid gap-4">
              {/* Pending Family Trees */}
              {familyTrees.filter(f => f.status === "pending").length > 0 && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Clock className="w-5 h-5 text-amber-500" />
                      Pending Verification ({familyTrees.filter(f => f.status === "pending").length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {familyTrees.filter(f => f.status === "pending").map(tree => (
                      <div key={tree.id} className="p-4 border rounded-lg bg-amber-50/50 dark:bg-amber-950/20">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h4 className="font-semibold">{tree.family_name}</h4>
                            <p className="text-sm text-muted-foreground">
                              {tree.members?.length || 0} members • Created {new Date(tree.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            {admin?.role === "master" && (
                              <Button 
                                size="sm" 
                                onClick={() => {
                                  setSelectedFamilyTree(tree);
                                  setShowVerifyFamilyDialog(true);
                                }}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Review
                              </Button>
                            )}
                            <Button 
                              size="sm" 
                              variant="destructive"
                              onClick={() => handleDeleteFamilyTree(tree.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {tree.members?.map((member, idx) => (
                            <Badge key={`mem-${member.name || idx}`} variant="outline" className="text-xs">
                              {member.name} ({member.relationship})
                            </Badge>
                          ))}
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Verified Family Trees */}
              {familyTrees.filter(f => f.status === "verified").length > 0 && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      Verified Families ({familyTrees.filter(f => f.status === "verified").length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {familyTrees.filter(f => f.status === "verified").map(tree => (
                      <div key={tree.id} className="p-4 border rounded-lg bg-green-50/50 dark:bg-green-950/20">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <div className="flex items-center gap-2">
                              <h4 className="font-semibold">{tree.family_name}</h4>
                              <Badge className="bg-green-600">{tree.family_id}</Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {tree.members?.length || 0} members • Verified {tree.verified_at ? new Date(tree.verified_at).toLocaleDateString() : ""}
                            </p>
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleDeleteFamilyTree(tree.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {tree.members?.map((member, idx) => (
                            <Badge key={`fmem-${member.lf_id || idx}`} variant="secondary" className="text-xs">
                              {member.lf_id}: {member.name} ({member.relationship})
                            </Badge>
                          ))}
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Rejected Family Trees */}
              {familyTrees.filter(f => f.status === "rejected").length > 0 && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <XCircle className="w-5 h-5 text-red-500" />
                      Rejected ({familyTrees.filter(f => f.status === "rejected").length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {familyTrees.filter(f => f.status === "rejected").map(tree => (
                      <div key={tree.id} className="p-4 border rounded-lg bg-red-50/50 dark:bg-red-950/20">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h4 className="font-semibold">{tree.family_name}</h4>
                            <p className="text-sm text-muted-foreground">
                              {tree.members?.length || 0} members
                            </p>
                            {tree.rejection_note && (
                              <p className="text-sm text-red-600 mt-1">Reason: {tree.rejection_note}</p>
                            )}
                          </div>
                          <Button 
                            size="sm" 
                            variant="destructive"
                            onClick={() => handleDeleteFamilyTree(tree.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Empty State */}
              {familyTrees.length === 0 && (
                <Card>
                  <CardContent className="py-12 text-center text-muted-foreground">
                    <GitBranch className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <p className="text-lg mb-2">No Family Trees Yet</p>
                    <p className="text-sm">Create a family tree by adding verified users and their relationships.</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Popup Management Tab */}
          <TabsContent value="popup" className="space-y-6">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-semibold">Login Popup Image</h3>
                    <p className="text-sm text-muted-foreground">This image will pop up when users log in, before the welcome page.</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">{popupSettings.is_active ? "Active" : "Inactive"}</span>
                    <button
                      onClick={() => setPopupSettings(prev => ({ ...prev, is_active: !prev.is_active }))}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${popupSettings.is_active ? 'bg-primary' : 'bg-gray-300'}`}
                      data-testid="popup-toggle"
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${popupSettings.is_active ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  </div>
                </div>

                {/* Title Input */}
                <div className="mb-4">
                  <label className="text-sm font-medium mb-1 block">Popup Title (optional)</label>
                  <Input
                    value={popupSettings.title}
                    onChange={(e) => setPopupSettings(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="e.g. Sneh Sammelan 2026"
                    data-testid="popup-title-input"
                  />
                </div>

                {/* Image Upload */}
                <div className="mb-4">
                  <label className="text-sm font-medium mb-1 block">Popup Image</label>
                  <div className="border-2 border-dashed rounded-lg p-6 text-center">
                    {popupImagePreview ? (
                      <div className="space-y-3">
                        <img src={popupImagePreview} alt="Popup preview" className="max-w-sm mx-auto rounded-lg shadow" data-testid="popup-image-preview" />
                        <div className="flex justify-center gap-2">
                          <label className="cursor-pointer">
                            <Button variant="outline" size="sm" asChild>
                              <span>Change Image</span>
                            </Button>
                            <input type="file" accept="image/*" className="hidden" onChange={handlePopupImageUpload} data-testid="popup-image-input-change" />
                          </label>
                          <Button variant="destructive" size="sm" onClick={() => { setPopupImagePreview(""); setPopupSettings(prev => ({ ...prev, image: "" })); }}>
                            Remove
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <label className="cursor-pointer block py-8">
                        <ImageIcon className="w-12 h-12 mx-auto text-muted-foreground mb-2" />
                        <p className="text-sm text-muted-foreground mb-1">Click to upload an image</p>
                        <p className="text-xs text-muted-foreground">PNG, JPG up to 5MB</p>
                        <input type="file" accept="image/*" className="hidden" onChange={handlePopupImageUpload} data-testid="popup-image-input" />
                      </label>
                    )}
                  </div>
                </div>

                {/* Or paste URL */}
                <div className="mb-6">
                  <label className="text-sm font-medium mb-1 block">Or paste image URL</label>
                  <Input
                    value={popupSettings.image && !popupSettings.image.startsWith("data:") ? popupSettings.image : ""}
                    onChange={(e) => { setPopupSettings(prev => ({ ...prev, image: e.target.value })); setPopupImagePreview(e.target.value); }}
                    placeholder="https://example.com/image.jpg"
                    data-testid="popup-url-input"
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <Button onClick={handleSavePopup} disabled={popupLoading || !popupSettings.image} data-testid="popup-save-btn">
                    {popupLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    {popupSettings.is_active ? "Save & Activate" : "Save (Inactive)"}
                  </Button>
                  {popupSettings.image && (
                    <Button variant="destructive" onClick={handleRemovePopup} disabled={popupLoading} data-testid="popup-remove-btn">
                      Remove Popup
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Travel Forms Tab */}
          <TabsContent value="travel-forms" className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Travel Registrations ({travelFormsTotal})</h2>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={async () => {
                    try {
                      const response = await adminApi.get('/admin/travel-forms/export', {
                        responseType: 'blob'
                      });
                      const url = window.URL.createObjectURL(new Blob([response.data]));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', 'travel_registrations.xlsx');
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      window.URL.revokeObjectURL(url);
                      toast.success("Travel forms exported successfully!");
                    } catch (error) {
                      toast.error("Failed to export travel forms");
                      console.error(error);
                    }
                  }}
                  disabled={travelFormsTotal === 0}
                  data-testid="export-travel-forms"
                >
                  <Download className="w-4 h-4 mr-1" /> Export Excel
                </Button>
                <Button variant="outline" size="sm" onClick={() => {
                  const url = `${window.location.origin}/travel-form`;
                  navigator.clipboard.writeText(url);
                  toast.success("Form link copied!");
                }} data-testid="copy-form-link">
                  <Copy className="w-4 h-4 mr-1" /> Copy Form Link
                </Button>
              </div>
            </div>

            {travelForms.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center text-muted-foreground">
                  <Plane className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No travel registrations yet</p>
                  <p className="text-sm mt-1">Share the form link to start collecting registrations</p>
                </CardContent>
              </Card>
            ) : (
              travelForms.map((f) => {
                // Parse family members if it's a JSON string
                let familyMembersList = [];
                try {
                  if (typeof f.family_members === 'string') {
                    familyMembersList = JSON.parse(f.family_members);
                  } else if (Array.isArray(f.family_members)) {
                    familyMembersList = f.family_members;
                  }
                } catch {
                  // If parsing fails, show as-is
                  familyMembersList = [];
                }

                const formatGender = (g) => {
                  if (!g) return '';
                  return g === 'male' ? 'M' : g === 'female' ? 'F' : 'O';
                };

                return (
                <Card key={f.id} className="overflow-hidden">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-semibold text-base">{f.contact_name}</h3>
                        <p className="text-sm text-muted-foreground">
                          {f.contact_age && f.contact_gender && (
                            <span className="mr-2">({formatGender(f.contact_gender)}, {f.contact_age} yrs)</span>
                          )}
                          {f.mobile_number} | Emergency: {f.emergency_contact}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                          {new Date(f.submitted_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                        </Badge>
                        <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 h-7 w-7 p-0" onClick={() => handleDeleteTravelForm(f.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    <div className="text-sm mb-2">
                      <span className="font-medium">Family Members:</span>
                      {familyMembersList.length > 0 ? (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {familyMembersList.map((m, idx) => (
                            <Badge key={idx} variant="secondary" className="text-xs font-normal">
                              {m.name} ({formatGender(m.gender)}, {m.age})
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <p className="text-muted-foreground whitespace-pre-line">{f.family_members}</p>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                      {/* Outbound */}
                      <div className="p-3 bg-blue-50 rounded-lg text-sm">
                        <p className="font-medium text-blue-800 mb-1">Outbound Travel</p>
                        <p>{f.departure_from} → {f.destination_to}</p>
                        <p>Mode: {TRANSPORT_LABELS[f.transport_mode] || f.transport_mode}</p>
                        {f.flight_number && <p>Flight: {f.flight_number}</p>}
                        {f.train_number && <p>Train: {f.train_number}</p>}
                        {f.pickup_needed && (
                          <Badge className="mt-1 bg-orange-100 text-orange-800 text-xs">Pickup Needed</Badge>
                        )}
                        <p className="text-xs text-muted-foreground mt-1">
                          {f.travel_start && new Date(f.travel_start).toLocaleString("en-IN")} → {f.travel_end && new Date(f.travel_end).toLocaleString("en-IN")}
                        </p>
                      </div>
                      {/* Return */}
                      <div className="p-3 bg-green-50 rounded-lg text-sm">
                        <p className="font-medium text-green-800 mb-1">Return Travel</p>
                        <p>{f.return_from} → {f.return_to}</p>
                        <p>Mode: {TRANSPORT_LABELS[f.return_transport_mode] || f.return_transport_mode}</p>
                        {f.return_flight_number && <p>Flight: {f.return_flight_number}</p>}
                        {f.return_train_number && <p>Train: {f.return_train_number}</p>}
                        {f.drop_needed && (
                          <Badge className="mt-1 bg-green-100 text-green-800 text-xs">Drop Needed</Badge>
                        )}
                        <p className="text-xs text-muted-foreground mt-1">
                          {f.return_start && new Date(f.return_start).toLocaleString("en-IN")} → {f.return_end && new Date(f.return_end).toLocaleString("en-IN")}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )})
            )}
          </TabsContent>

          {/* Kuldevi Forms Tab */}
          <TabsContent value="kuldevi-forms" className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">कुलदेवी साक्ष्य फॉर्म ({kuldeviFormsTotal})</h2>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={async () => {
                    try {
                      const response = await adminApi.get('/admin/kuldevi-forms/export', {
                        responseType: 'blob'
                      });
                      const url = window.URL.createObjectURL(new Blob([response.data]));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', 'kuldevi_forms.xlsx');
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      window.URL.revokeObjectURL(url);
                      toast.success("Kuldevi forms exported successfully!");
                    } catch (error) {
                      toast.error("Failed to export kuldevi forms");
                      console.error(error);
                    }
                  }}
                  disabled={kuldeviFormsTotal === 0}
                  data-testid="export-kuldevi-forms"
                >
                  <Download className="w-4 h-4 mr-1" /> Export Excel
                </Button>
                <Button variant="outline" size="sm" onClick={() => {
                  const url = `${window.location.origin}/kuldevi-form`;
                  navigator.clipboard.writeText(url);
                  toast.success("Form link copied!");
                }} data-testid="copy-kuldevi-form-link">
                  <Copy className="w-4 h-4 mr-1" /> Copy Form Link
                </Button>
              </div>
            </div>

            {kuldeviForms.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center text-muted-foreground">
                  <BookOpen className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No Kuldevi form submissions yet</p>
                  <p className="text-sm mt-1">Share the form link to start collecting submissions</p>
                </CardContent>
              </Card>
            ) : (
              kuldeviForms.map((f) => (
                <Card key={f.id} className="overflow-hidden">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-semibold text-base">{f.full_name}</h3>
                        <p className="text-sm text-muted-foreground">
                          {f.mobile_number} | LF ID: {f.lf_id}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {f.current_residence} | मूल गाँव: {f.ancestral_village}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                          {new Date(f.submitted_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                        </Badge>
                        <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 h-7 w-7 p-0" onClick={() => handleDeleteKuldeviForm(f.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                      {/* Kuldevi Info */}
                      <div className="p-3 bg-orange-50 rounded-lg text-sm">
                        <p className="font-medium text-orange-800 mb-1">कुलदेवी विवरण</p>
                        <p><strong>नाम:</strong> {f.kuldevi_name}</p>
                        {f.kuldevi_avatar && <p><strong>स्वरूप:</strong> {f.kuldevi_avatar}</p>}
                        <p><strong>मंदिर:</strong> {f.main_temple_location}</p>
                        {f.traditions && <p className="text-xs mt-1"><strong>परंपराएं:</strong> {f.traditions}</p>}
                      </div>
                      {/* Evidence Info */}
                      <div className="p-3 bg-blue-50 rounded-lg text-sm">
                        <p className="font-medium text-blue-800 mb-1">साक्ष्य विवरण</p>
                        <p><strong>प्रकार:</strong> {EVIDENCE_TYPE_LABELS[f.evidence_type] || f.evidence_type}</p>
                        {f.evidence_period && <p><strong>काल:</strong> {f.evidence_period}</p>}
                        <p><strong>संरक्षक:</strong> {f.evidence_custodian}</p>
                        <p className="text-xs mt-1 line-clamp-2"><strong>विवरण:</strong> {f.evidence_description}</p>
                      </div>
                    </div>

                    {/* Evidence Files */}
                    {f.evidence_files && f.evidence_files.length > 0 && (
                      <div className="mt-3 p-2 bg-gray-50 rounded-lg">
                        <p className="text-xs font-medium mb-2">अपलोड किए गए दस्तावेज ({f.evidence_files.length}):</p>
                        <div className="flex flex-wrap gap-2">
                          {f.evidence_files.map((fileUrl, idx) => {
                            const isImage = fileUrl.startsWith('data:image/');
                            return (
                              <div key={idx} className="flex flex-col items-center gap-1">
                                {isImage ? (
                                  <a 
                                    href={fileUrl}
                                    download={`evidence_${idx + 1}.jpg`}
                                    className="block"
                                  >
                                    <img 
                                      src={fileUrl} 
                                      alt={`Evidence ${idx + 1}`}
                                      className="w-16 h-16 object-cover rounded border hover:opacity-80 cursor-pointer"
                                    />
                                  </a>
                                ) : (
                                  <a 
                                    href={fileUrl}
                                    download={`evidence_${idx + 1}`}
                                    className="text-xs text-blue-600 hover:underline flex items-center gap-1 bg-white px-2 py-1 rounded border"
                                  >
                                    <FileText className="w-3 h-3" />
                                    File {idx + 1}
                                  </a>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Purohit Details if present */}
                    {f.purohit_details && (
                      <div className="mt-2 p-2 bg-purple-50 rounded text-xs">
                        <strong>पुरोहित विवरण:</strong> {f.purohit_details}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>


        </Tabs>
        </>
        )}

        {/* Create Family Tree Dialog */}
        <Dialog open={showCreateFamilyDialog} onOpenChange={setShowCreateFamilyDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <GitBranch className="w-5 h-5" />
                Create Family Tree
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Family Name *</label>
                <Input
                  placeholder="e.g., The Lunawat Family"
                  value={newFamilyTree.family_name}
                  onChange={(e) => setNewFamilyTree(prev => ({ ...prev, family_name: e.target.value }))}
                />
              </div>

              {/* Add Family Member */}
              <div className="space-y-2 p-4 border rounded-lg bg-muted/30">
                <h4 className="text-sm font-medium">Add Family Members</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <label className="text-xs font-medium">Search User by Name or LF ID</label>
                    <Input
                      placeholder="Search verified users..."
                      value={familyMemberSearch}
                      onChange={(e) => setFamilyMemberSearch(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-medium">Relationship *</label>
                    <Select value={selectedRelationship} onValueChange={setSelectedRelationship}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select relationship" />
                      </SelectTrigger>
                      <SelectContent>
                        {RELATIONSHIP_TYPES.map(rel => (
                          <SelectItem key={rel} value={rel}>{rel}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Search Results */}
                {familyMemberSearch && (
                  <div className="mt-2 max-h-40 overflow-y-auto border rounded-md">
                    {filteredUsersForFamily.length > 0 ? (
                      filteredUsersForFamily.slice(0, 10).map(user => (
                        <div 
                          key={user.id}
                          className="p-2 hover:bg-muted cursor-pointer flex justify-between items-center border-b last:border-b-0"
                          onClick={() => handleAddFamilyMember(user)}
                        >
                          <div className="flex items-center gap-2">
                            <Avatar className="w-8 h-8">
                              <AvatarImage src={user.avatar} />
                              <AvatarFallback className="text-xs">
                                {user.first_name?.[0]}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="text-sm font-medium">
                                {[user.first_name, user.middle_name, user.last_name].filter(Boolean).join(" ")}
                              </p>
                              <p className="text-xs text-muted-foreground">{user.member_code}</p>
                            </div>
                          </div>
                          <Plus className="w-4 h-4 text-muted-foreground" />
                        </div>
                      ))
                    ) : (
                      <p className="p-2 text-sm text-muted-foreground text-center">No users found</p>
                    )}
                  </div>
                )}
              </div>

              {/* Added Members */}
              {newFamilyTree.members.length > 0 && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">Family Members ({newFamilyTree.members.length})</label>
                  <div className="space-y-2">
                    {newFamilyTree.members.map((member, index) => (
                      <div 
                        key={`new-mem-${member.lf_id || member.name || index}`}
                        className="p-3 border rounded-lg flex justify-between items-center bg-primary/5"
                      >
                        <div className="flex items-center gap-3">
                          <Avatar className="w-10 h-10">
                            <AvatarImage src={member.avatar} />
                            <AvatarFallback>{member.name?.[0]}</AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium">{member.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {member.lf_id} • {member.relationship}
                            </p>
                          </div>
                        </div>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => handleRemoveFamilyMember(index)}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateFamilyDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateFamilyTree} disabled={familyLoading}>
                {familyLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <GitBranch className="w-4 h-4 mr-2" />
                    Submit for Verification
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Verify Family Tree Dialog */}
        <Dialog open={showVerifyFamilyDialog} onOpenChange={setShowVerifyFamilyDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Review Family Tree</DialogTitle>
            </DialogHeader>
            {selectedFamilyTree && (
              <div className="space-y-4 py-4">
                <div>
                  <h4 className="font-semibold text-lg">{selectedFamilyTree.family_name}</h4>
                  <p className="text-sm text-muted-foreground">
                    Created {new Date(selectedFamilyTree.created_at).toLocaleDateString()}
                  </p>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Members ({selectedFamilyTree.members?.length})</label>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {selectedFamilyTree.members?.map((member, idx) => (
                      <div key={`sel-mem-${member.lf_id || idx}`} className="p-2 border rounded flex items-center gap-2">
                        <Badge variant="outline">{member.lf_id}</Badge>
                        <span className="font-medium">{member.name}</span>
                        <span className="text-muted-foreground">({member.relationship})</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Rejection Note (if rejecting)</label>
                  <Textarea
                    placeholder="Enter reason for rejection..."
                    value={familyRejectionNote}
                    onChange={(e) => setFamilyRejectionNote(e.target.value)}
                    rows={2}
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowVerifyFamilyDialog(false)}>
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={() => handleVerifyFamilyTree("rejected")}
                disabled={familyLoading}
              >
                Reject
              </Button>
              <Button 
                onClick={() => handleVerifyFamilyTree("verified")}
                disabled={familyLoading}
              >
                {familyLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Verify & Assign Family ID
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* User Detail Dialog */}
        <Dialog open={showUserDialog} onOpenChange={setShowUserDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            {selectedUser && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-3">
                    <Avatar className="w-12 h-12">
                      <AvatarImage src={selectedUser.avatar} />
                      <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                        {getInitials(selectedUser)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <span>
                        {selectedUser.prefix && `${selectedUser.prefix}. `}
                        {selectedUser.first_name} {selectedUser.last_name || "LUNAWAT"}
                      </span>
                      <div className="mt-1">{getStatusBadge(selectedUser.verification_status)}</div>
                    </div>
                  </DialogTitle>
                  <DialogDescription>
                    Registered on {formatDate(selectedUser.created_at)}
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                  <div>
                    <h4 className="font-semibold mb-2 text-sm text-muted-foreground uppercase">Personal Information</h4>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div><span className="text-muted-foreground">Phone:</span> <span className="font-medium">{selectedUser.phone}</span></div>
                      <div><span className="text-muted-foreground">Gender:</span> <span className="font-medium capitalize">{selectedUser.gender || "N/A"}</span></div>
                      <div><span className="text-muted-foreground">DOB:</span> <span className="font-medium">{formatDate(selectedUser.date_of_birth)}</span></div>
                      <div><span className="text-muted-foreground">Username:</span> <span className="font-medium">@{selectedUser.username || "N/A"}</span></div>
                      {selectedUser.blood_group && <div><span className="text-muted-foreground">Blood Group:</span> <span className="font-medium">{selectedUser.blood_group}</span></div>}
                      {selectedUser.native_place && <div><span className="text-muted-foreground">Native Place:</span> <span className="font-medium">{selectedUser.native_place}</span></div>}
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-2 text-sm text-muted-foreground uppercase">Family Information</h4>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div><span className="text-muted-foreground">Marital Status:</span> <span className="font-medium capitalize">{selectedUser.marital_status || "N/A"}</span></div>
                      {selectedUser.spouse_name && <div><span className="text-muted-foreground">Spouse:</span> <span className="font-medium">{selectedUser.spouse_name}</span></div>}
                      {selectedUser.date_of_marriage && <div><span className="text-muted-foreground">Marriage Date:</span> <span className="font-medium">{formatDate(selectedUser.date_of_marriage)}</span></div>}
                      <div><span className="text-muted-foreground">Children:</span> <span className="font-medium">{selectedUser.number_of_children || 0}</span></div>
                      {selectedUser.father_name && <div><span className="text-muted-foreground">Father:</span> <span className="font-medium">{selectedUser.father_name}</span></div>}
                      {selectedUser.mother_name && <div><span className="text-muted-foreground">Mother:</span> <span className="font-medium">{selectedUser.mother_name}</span></div>}
                    </div>
                    {/* Children Details */}
                    {selectedUser.children && selectedUser.children.length > 0 && (
                      <div className="mt-3 space-y-2">
                        <h5 className="text-xs font-medium text-muted-foreground">Children Details</h5>
                        <div className="space-y-2">
                          {selectedUser.children.map((child, idx) => (
                            <div key={`child-${child.name || idx}`} className="p-2 bg-muted/30 rounded text-sm flex flex-wrap gap-x-4 gap-y-1">
                              <span className="font-medium">{idx + 1}. {child.name || "Unnamed"}</span>
                              {child.gender && <span className="capitalize text-muted-foreground">({child.gender})</span>}
                              {child.age && <span className="text-muted-foreground">Age: {child.age}</span>}
                              {child.education && <span className="text-muted-foreground capitalize">Education: {child.education.replace('-', ' ')}</span>}
                              {child.marital_status && <span className="text-muted-foreground capitalize">Status: {child.marital_status}</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    <h4 className="font-semibold mb-2 text-sm text-muted-foreground uppercase">Address</h4>
                    <p className="text-sm">
                      {selectedUser.is_overseas && <Badge variant="outline" className="mr-2 mb-1">Overseas</Badge>}
                      {selectedUser.address_line1 ? (
                        <>
                          {selectedUser.address_line1}
                          {selectedUser.address_line2 && <>, {selectedUser.address_line2}</>}
                          <br />
                          {selectedUser.city}, {selectedUser.state} {selectedUser.pincode && `- ${selectedUser.pincode}`}
                          <br />
                          {selectedUser.country}
                        </>
                      ) : "Not provided"}
                    </p>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-2 text-sm text-muted-foreground uppercase">Business Information</h4>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div><span className="text-muted-foreground">Occupation:</span> <span className="font-medium capitalize">{selectedUser.occupation || "N/A"}</span></div>
                      {selectedUser.company_name && <div><span className="text-muted-foreground">Company:</span> <span className="font-medium">{selectedUser.company_name}</span></div>}
                      {selectedUser.designation && <div><span className="text-muted-foreground">Designation:</span> <span className="font-medium">{selectedUser.designation}</span></div>}
                      {selectedUser.work_address && <div className="col-span-2"><span className="text-muted-foreground">Work Address:</span> <span className="font-medium">{selectedUser.work_address}</span></div>}
                    </div>
                  </div>

                  {selectedUser.bio && (
                    <div>
                      <h4 className="font-semibold mb-2 text-sm text-muted-foreground uppercase">Bio</h4>
                      <p className="text-sm">{selectedUser.bio}</p>
                    </div>
                  )}

                  {selectedUser.verification_status === "pending" && (
                    <div>
                      <h4 className="font-semibold mb-2 text-sm text-muted-foreground uppercase">Rejection Note (if rejecting)</h4>
                      <Textarea
                        placeholder="Enter reason for rejection (optional)"
                        value={rejectionNote}
                        onChange={(e) => setRejectionNote(e.target.value)}
                        rows={2}
                      />
                    </div>
                  )}
                </div>

                <DialogFooter>
                  {selectedUser.verification_status === "pending" && (
                    <>
                      <Button
                        variant="destructive"
                        onClick={() => handleVerify(selectedUser.id, "rejected", rejectionNote)}
                        disabled={actionLoading}
                      >
                        {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <XCircle className="w-4 h-4 mr-2" />}
                        Reject
                      </Button>
                      <Button
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => handleVerify(selectedUser.id, "verified")}
                        disabled={actionLoading}
                      >
                        {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                        Verify User
                      </Button>
                    </>
                  )}
                  <Button variant="outline" onClick={() => setShowUserDialog(false)}>
                    Close
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Create Admin Dialog */}
        <Dialog open={showAdminDialog} onOpenChange={setShowAdminDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Admin</DialogTitle>
              <DialogDescription>
                Add a new administrator to manage the platform.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Name</label>
                <Input
                  placeholder="Full name"
                  value={newAdmin.name}
                  onChange={(e) => setNewAdmin(p => ({ ...p, name: e.target.value }))}
                  data-testid="new-admin-name"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Username</label>
                <Input
                  placeholder="Login username"
                  value={newAdmin.username}
                  onChange={(e) => setNewAdmin(p => ({ ...p, username: e.target.value }))}
                  data-testid="new-admin-username"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Password</label>
                <Input
                  type="password"
                  placeholder="Login password"
                  value={newAdmin.password}
                  onChange={(e) => setNewAdmin(p => ({ ...p, password: e.target.value }))}
                  data-testid="new-admin-password"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAdminDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateAdmin} disabled={actionLoading} data-testid="confirm-create-admin">
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
                Create Admin
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Prefix Dialog */}
        <Dialog open={showPrefixDialog} onOpenChange={setShowPrefixDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Prefix</DialogTitle>
              <DialogDescription>
                Add a new name prefix option for users.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Label (Display Name)</label>
                <Input
                  placeholder="e.g., Prof, Adv, Col"
                  value={newPrefix.label}
                  onChange={(e) => setNewPrefix(p => ({ ...p, label: e.target.value }))}
                  data-testid="new-prefix-label"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Value (Internal)</label>
                <Input
                  placeholder="e.g., prof, adv, col"
                  value={newPrefix.value}
                  onChange={(e) => setNewPrefix(p => ({ ...p, value: e.target.value.toLowerCase() }))}
                  data-testid="new-prefix-value"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPrefixDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreatePrefix} disabled={actionLoading} data-testid="confirm-create-prefix">
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                Add Prefix
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit User Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit className="w-5 h-5 text-primary" />
                Edit User Profile
              </DialogTitle>
              <DialogDescription>
                Modify user details. Changes will be saved immediately.
              </DialogDescription>
            </DialogHeader>
            
            {editUserData && (
              <div className="space-y-6 py-4">
                {/* Account Information */}
                <div>
                  <h4 className="font-semibold mb-3 text-sm text-muted-foreground uppercase">Account Information</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Phone Number</label>
                      <Input
                        value={editUserData.phone || ''}
                        onChange={(e) => updateEditField('phone', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Verification Status</label>
                      <Select value={editUserData.verification_status || ''} onValueChange={(v) => updateEditField('verification_status', v)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="verified">Verified</SelectItem>
                          <SelectItem value="rejected">Rejected</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Personal Information */}
                <div>
                  <h4 className="font-semibold mb-3 text-sm text-muted-foreground uppercase">Personal Information</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Prefix</label>
                      <Select value={editUserData.prefix || ''} onValueChange={(v) => updateEditField('prefix', v)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select prefix" />
                        </SelectTrigger>
                        <SelectContent>
                          {prefixes.map(p => (
                            <SelectItem key={p.id} value={p.label}>{p.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">First Name</label>
                      <Input
                        value={editUserData.first_name || ''}
                        onChange={(e) => updateEditField('first_name', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Middle Name</label>
                      <Input
                        value={editUserData.middle_name || ''}
                        onChange={(e) => updateEditField('middle_name', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Last Name</label>
                      <Input
                        value={editUserData.last_name || 'LUNAWAT'}
                        onChange={(e) => updateEditField('last_name', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Username</label>
                      <Input
                        value={editUserData.username || ''}
                        onChange={(e) => updateEditField('username', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Date of Birth</label>
                      <Input
                        type="date"
                        value={editUserData.date_of_birth || ''}
                        onChange={(e) => updateEditField('date_of_birth', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Gender</label>
                      <Select value={editUserData.gender || ''} onValueChange={(v) => updateEditField('gender', v)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select gender" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="male">Male</SelectItem>
                          <SelectItem value="female">Female</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Blood Group</label>
                      <Select value={editUserData.blood_group || ''} onValueChange={(v) => updateEditField('blood_group', v)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select blood group" />
                        </SelectTrigger>
                        <SelectContent>
                          {BLOOD_GROUPS.map(bg => (
                            <SelectItem key={bg} value={bg}>{bg}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2 col-span-2">
                      <label className="text-sm font-medium">Native Place (Rajasthan)</label>
                      <Select value={editUserData.native_place || ''} onValueChange={(v) => updateEditField('native_place', v)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select native place" />
                        </SelectTrigger>
                        <SelectContent>
                          {RAJASTHAN_PLACES.map(place => (
                            <SelectItem key={place} value={place}>{place}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Family Information */}
                <div>
                  <h4 className="font-semibold mb-3 text-sm text-muted-foreground uppercase">Family Information</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Marital Status</label>
                      <Select value={editUserData.marital_status || ''} onValueChange={(v) => updateEditField('marital_status', v)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="single">Single</SelectItem>
                          <SelectItem value="married">Married</SelectItem>
                          <SelectItem value="divorced">Divorced</SelectItem>
                          <SelectItem value="widowed">Widowed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">
                        {editUserData.gender === 'female' ? "Husband's Name" : "Spouse Name"}
                      </label>
                      <Input
                        value={editUserData.spouse_name || ''}
                        onChange={(e) => updateEditField('spouse_name', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Date of Marriage</label>
                      <Input
                        type="date"
                        value={editUserData.date_of_marriage || ''}
                        onChange={(e) => updateEditField('date_of_marriage', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Number of Children</label>
                      <Select value={(editUserData.number_of_children || 0).toString()} onValueChange={handleEditChildrenCountChange}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                            <SelectItem key={n} value={n.toString()}>{n}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Father&apos;s Name</label>
                      <Input
                        value={editUserData.father_name || ''}
                        onChange={(e) => updateEditField('father_name', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Mother&apos;s Name</label>
                      <Input
                        value={editUserData.mother_name || ''}
                        onChange={(e) => updateEditField('mother_name', e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Children Details */}
                  {editUserData.number_of_children > 0 && editUserData.children && (
                    <div className="mt-4 space-y-3">
                      <h5 className="text-sm font-medium text-muted-foreground">Children Details</h5>
                      {editUserData.children.map((child, index) => (
                        <div key={`edit-child-${child.name || index}`} className="p-3 border rounded-lg bg-muted/30 space-y-3">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-bold">
                              {index + 1}
                            </span>
                            <span className="font-medium text-sm">Child {index + 1}</span>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                              <label className="text-xs font-medium">Name</label>
                              <Input
                                placeholder="Name"
                                value={child.name || ''}
                                onChange={(e) => updateEditChildField(index, 'name', e.target.value)}
                              />
                            </div>
                            <div className="space-y-1">
                              <label className="text-xs font-medium">Gender</label>
                              <Select value={child.gender || ''} onValueChange={(v) => updateEditChildField(index, 'gender', v)}>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="male">Male</SelectItem>
                                  <SelectItem value="female">Female</SelectItem>
                                  <SelectItem value="other">Other</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-1">
                              <label className="text-xs font-medium">Age</label>
                              <Input
                                type="number"
                                min="0"
                                max="100"
                                placeholder="Age"
                                value={child.age || ''}
                                onChange={(e) => updateEditChildField(index, 'age', e.target.value)}
                              />
                            </div>
                            <div className="space-y-1">
                              <label className="text-xs font-medium">Marital Status</label>
                              <Select value={child.marital_status || ''} onValueChange={(v) => updateEditChildField(index, 'marital_status', v)}>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="unmarried">Unmarried</SelectItem>
                                  <SelectItem value="married">Married</SelectItem>
                                  <SelectItem value="divorced">Divorced</SelectItem>
                                  <SelectItem value="widowed">Widowed</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-1 col-span-2">
                              <label className="text-xs font-medium">Education</label>
                              <Select value={child.education || ''} onValueChange={(v) => updateEditChildField(index, 'education', v)}>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select education" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="pre-school">Pre-School</SelectItem>
                                  <SelectItem value="primary">Primary School (1-5)</SelectItem>
                                  <SelectItem value="middle">Middle School (6-8)</SelectItem>
                                  <SelectItem value="high-school">High School (9-10)</SelectItem>
                                  <SelectItem value="senior-secondary">Senior Secondary (11-12)</SelectItem>
                                  <SelectItem value="undergraduate">Undergraduate</SelectItem>
                                  <SelectItem value="graduate">Graduate</SelectItem>
                                  <SelectItem value="post-graduate">Post Graduate</SelectItem>
                                  <SelectItem value="doctorate">Doctorate/PhD</SelectItem>
                                  <SelectItem value="professional">Professional Degree</SelectItem>
                                  <SelectItem value="other">Other</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Address */}
                <div>
                  <h4 className="font-semibold mb-3 text-sm text-muted-foreground uppercase">Address</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={editUserData.is_overseas || false}
                          onChange={(e) => updateEditField('is_overseas', e.target.checked)}
                          className="rounded"
                        />
                        Overseas Resident
                      </label>
                    </div>
                    <div></div>
                    <div className="space-y-2 col-span-2">
                      <label className="text-sm font-medium">Address Line 1</label>
                      <Input
                        value={editUserData.address_line1 || ''}
                        onChange={(e) => updateEditField('address_line1', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2 col-span-2">
                      <label className="text-sm font-medium">Address Line 2</label>
                      <Input
                        value={editUserData.address_line2 || ''}
                        onChange={(e) => updateEditField('address_line2', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">City</label>
                      <Input
                        value={editUserData.city || ''}
                        onChange={(e) => updateEditField('city', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">State</label>
                      <Select value={editUserData.state || ''} onValueChange={(v) => updateEditField('state', v)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select state" />
                        </SelectTrigger>
                        <SelectContent>
                          {INDIAN_STATES.map(state => (
                            <SelectItem key={state} value={state}>{state}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Pincode</label>
                      <Input
                        value={editUserData.pincode || ''}
                        onChange={(e) => updateEditField('pincode', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Country</label>
                      <Input
                        value={editUserData.country || ''}
                        onChange={(e) => updateEditField('country', e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* Business Information */}
                <div>
                  <h4 className="font-semibold mb-3 text-sm text-muted-foreground uppercase">Business Information</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Occupation</label>
                      <Select value={editUserData.occupation || ''} onValueChange={(v) => updateEditField('occupation', v)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select occupation" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="employed">Employed</SelectItem>
                          <SelectItem value="self-employed">Self Employed</SelectItem>
                          <SelectItem value="business">Business Owner</SelectItem>
                          <SelectItem value="student">Student</SelectItem>
                          <SelectItem value="homemaker">Homemaker</SelectItem>
                          <SelectItem value="retired">Retired</SelectItem>
                          <SelectItem value="freelancer">Freelancer</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Company Name</label>
                      <Input
                        value={editUserData.company_name || ''}
                        onChange={(e) => updateEditField('company_name', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Designation</label>
                      <Input
                        value={editUserData.designation || ''}
                        onChange={(e) => updateEditField('designation', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Work Address</label>
                      <Input
                        value={editUserData.work_address || ''}
                        onChange={(e) => updateEditField('work_address', e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* Bio */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Bio</label>
                  <Textarea
                    value={editUserData.bio || ''}
                    onChange={(e) => updateEditField('bio', e.target.value)}
                    rows={3}
                  />
                </div>
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveUser} disabled={actionLoading} data-testid="save-user-btn">
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Poll Dialog */}
        <Dialog open={showPollDialog} onOpenChange={setShowPollDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Vote className="w-5 h-5" />
                Create New Poll
              </DialogTitle>
              <DialogDescription>
                Create a poll for users to vote on
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Question</label>
                <Textarea
                  placeholder="Enter your poll question..."
                  value={newPoll.question}
                  onChange={(e) => setNewPoll(prev => ({ ...prev, question: e.target.value }))}
                  rows={2}
                />
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Options</label>
                <div className="space-y-2">
                  {newPoll.options.map((option, index) => (
                    <div key={`poll-opt-${index}`} className="flex gap-2">
                      <Input
                        placeholder={`Option ${index + 1}`}
                        value={option}
                        onChange={(e) => updatePollOption(index, e.target.value)}
                      />
                      {newPoll.options.length > 2 && (
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => removePollOption(index)}
                          className="text-red-500 hover:text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={addPollOption}
                  className="mt-2"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Option
                </Button>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPollDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreatePoll} disabled={pollLoading}>
                {pollLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  "Create Poll"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create/Edit Update Dialog */}
        <Dialog open={showUpdateDialog} onOpenChange={setShowUpdateDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Bell className="w-5 h-5" />
                {editingUpdate ? "Edit Update" : "Create New Update"}
              </DialogTitle>
              <DialogDescription>
                Create an update to share announcements with users
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Title</label>
                <Input
                  placeholder="Enter update title..."
                  value={newUpdate.title}
                  onChange={(e) => setNewUpdate(prev => ({ ...prev, title: e.target.value }))}
                />
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Content</label>
                <div className="border rounded-md">
                  {/* Simple formatting toolbar */}
                  <div className="flex gap-1 p-2 border-b bg-muted/50">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const textarea = document.getElementById('update-content');
                        const start = textarea.selectionStart;
                        const end = textarea.selectionEnd;
                        const text = newUpdate.content;
                        const selectedText = text.substring(start, end);
                        const newText = text.substring(0, start) + `<b>${selectedText}</b>` + text.substring(end);
                        setNewUpdate(prev => ({ ...prev, content: newText }));
                      }}
                      className="font-bold"
                    >
                      B
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const textarea = document.getElementById('update-content');
                        const start = textarea.selectionStart;
                        const end = textarea.selectionEnd;
                        const text = newUpdate.content;
                        const selectedText = text.substring(start, end);
                        const newText = text.substring(0, start) + `<i>${selectedText}</i>` + text.substring(end);
                        setNewUpdate(prev => ({ ...prev, content: newText }));
                      }}
                      className="italic"
                    >
                      I
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const textarea = document.getElementById('update-content');
                        const start = textarea.selectionStart;
                        const end = textarea.selectionEnd;
                        const text = newUpdate.content;
                        const selectedText = text.substring(start, end);
                        const newText = text.substring(0, start) + `<u>${selectedText}</u>` + text.substring(end);
                        setNewUpdate(prev => ({ ...prev, content: newText }));
                      }}
                      className="underline"
                    >
                      U
                    </Button>
                    <div className="w-px bg-border mx-1" />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setNewUpdate(prev => ({ ...prev, content: prev.content + '\n<ul>\n<li></li>\n</ul>' }));
                      }}
                    >
                      • List
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const url = prompt('Enter link URL:');
                        if (url) {
                          const textarea = document.getElementById('update-content');
                          const start = textarea.selectionStart;
                          const end = textarea.selectionEnd;
                          const text = newUpdate.content;
                          const selectedText = text.substring(start, end) || 'Link text';
                          const newText = text.substring(0, start) + `<a href="${url}" target="_blank">${selectedText}</a>` + text.substring(end);
                          setNewUpdate(prev => ({ ...prev, content: newText }));
                        }
                      }}
                    >
                      🔗 Link
                    </Button>
                  </div>
                  <Textarea
                    id="update-content"
                    placeholder="Enter update content... You can use HTML tags for formatting."
                    value={newUpdate.content}
                    onChange={(e) => setNewUpdate(prev => ({ ...prev, content: e.target.value }))}
                    rows={8}
                    className="border-0 focus-visible:ring-0"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Tip: Select text and click formatting buttons, or write HTML directly.
                </p>
              </div>

              {/* Preview */}
              {newUpdate.content && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">Preview</label>
                  <div 
                    className="p-3 border rounded-md bg-muted/30 prose prose-sm max-w-none dark:prose-invert"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(newUpdate.content) }}
                  />
                </div>
              )}
              
              {/* Image Upload */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Images (Optional)</label>
                <div className="border-2 border-dashed rounded-lg p-4">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                    className="hidden"
                    id="update-images"
                  />
                  <label 
                    htmlFor="update-images" 
                    className="flex flex-col items-center cursor-pointer"
                  >
                    <ImageIcon className="w-8 h-8 text-muted-foreground mb-2" />
                    <span className="text-sm text-muted-foreground">
                      Click to upload images (max 10, 5MB each)
                    </span>
                  </label>
                </div>
                
                {/* Image Previews */}
                {imagePreview.length > 0 && (
                  <div className="grid grid-cols-5 gap-2 mt-2">
                    {imagePreview.map((img, idx) => (
                      <div key={`preview-${idx}-${img.slice(-15)}`} className="relative group">
                        <img 
                          src={img} 
                          alt={`Preview ${idx + 1}`} 
                          className="w-full aspect-square object-cover rounded"
                        />
                        <Button
                          type="button"
                          variant="destructive"
                          size="icon"
                          className="absolute top-1 right-1 w-6 h-6 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => removeImage(idx)}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowUpdateDialog(false); setEditingUpdate(null); }}>
                Cancel
              </Button>
              <Button onClick={handleCreateUpdate} disabled={updateLoading}>
                {updateLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {editingUpdate ? "Saving..." : "Creating..."}
                  </>
                ) : (
                  editingUpdate ? "Save Changes" : "Create Update"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete User Confirmation Dialog */}
        <Dialog open={showDeleteUserDialog} onOpenChange={setShowDeleteUserDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <Trash2 className="w-5 h-5" />
                Delete User Permanently?
              </DialogTitle>
              <DialogDescription className="pt-2">
                This action cannot be undone. The user profile and all associated data will be permanently deleted.
              </DialogDescription>
            </DialogHeader>
            
            {userToDelete && (
              <div className="bg-muted/50 rounded-lg p-4 my-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-lg font-semibold">
                    {userToDelete.first_name?.[0] || userToDelete.phone?.slice(-2)}
                  </div>
                  <div>
                    <p className="font-medium">
                      {userToDelete.prefix} {userToDelete.first_name} {userToDelete.last_name}
                    </p>
                    <p className="text-sm text-muted-foreground">{userToDelete.phone}</p>
                    {userToDelete.city && (
                      <p className="text-xs text-muted-foreground">{userToDelete.city}, {userToDelete.state}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className="text-sm text-red-800 dark:text-red-200 font-medium">
                The following will be deleted:
              </p>
              <ul className="text-sm text-red-700 dark:text-red-300 mt-2 list-disc list-inside space-y-1">
                <li>User profile information</li>
                <li>All messages and conversations</li>
                <li>Poll votes</li>
                <li>All associated data</li>
              </ul>
            </div>

            <DialogFooter className="gap-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowDeleteUserDialog(false);
                  setUserToDelete(null);
                }}
                disabled={deleteLoading}
              >
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleDeleteUser}
                disabled={deleteLoading}
              >
                {deleteLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete User
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Change Credentials Dialog */}
        <Dialog open={showCredentialsDialog} onOpenChange={setShowCredentialsDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Key className="w-5 h-5" />
                Change Admin Credentials
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Current Password *</label>
                <Input
                  type="password"
                  placeholder="Enter current password"
                  value={credentialsData.current_password}
                  onChange={(e) => setCredentialsData(prev => ({ ...prev, current_password: e.target.value }))}
                />
              </div>
              <div className="border-t pt-4">
                <p className="text-sm text-muted-foreground mb-3">Leave blank to keep unchanged</p>
                <div className="space-y-3">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">New Username</label>
                    <Input
                      placeholder="Enter new username"
                      value={credentialsData.new_username}
                      onChange={(e) => setCredentialsData(prev => ({ ...prev, new_username: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">New Password</label>
                    <Input
                      type="password"
                      placeholder="Enter new password (min 6 chars)"
                      value={credentialsData.new_password}
                      onChange={(e) => setCredentialsData(prev => ({ ...prev, new_password: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Confirm New Password</label>
                    <Input
                      type="password"
                      placeholder="Confirm new password"
                      value={credentialsData.confirm_password}
                      onChange={(e) => setCredentialsData(prev => ({ ...prev, confirm_password: e.target.value }))}
                    />
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCredentialsDialog(false)} disabled={credentialsLoading}>
                Cancel
              </Button>
              <Button onClick={handleChangeCredentials} disabled={credentialsLoading}>
                {credentialsLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Updating...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Update Credentials
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Likes Dialog - View who liked an update */}
        <Dialog open={showLikesDialog} onOpenChange={setShowLikesDialog}>
          <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Heart className="w-5 h-5 text-red-500" />
                Likes for &quot;{selectedUpdateLikes.update?.title}&quot;
              </DialogTitle>
              <DialogDescription>
                {selectedUpdateLikes.likes.length} {selectedUpdateLikes.likes.length === 1 ? 'person' : 'people'} liked this update
              </DialogDescription>
            </DialogHeader>
            
            {likesLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : selectedUpdateLikes.likes.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Heart className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p>No likes yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {selectedUpdateLikes.likes.map((user) => (
                  <div key={user.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50">
                    <Avatar className="w-10 h-10">
                      <AvatarImage src={user.avatar} />
                      <AvatarFallback className="bg-primary/10 text-primary text-sm">
                        {user.first_name?.[0] || user.phone?.slice(-2) || "?"}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">
                        {user.first_name} {user.last_name || "LUNAWAT"}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Phone className="w-3 h-3" />
                        {user.phone}
                        {user.member_code && (
                          <>
                            <span>•</span>
                            <Badge variant="outline" className="text-xs">
                              {user.member_code}
                            </Badge>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowLikesDialog(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default AdminPanelPage;
